import { useState, useEffect, useRef, useCallback } from 'react';
import { siteEmploye, libelleSite } from '../utils/organisation';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { Pencil, Save, User, Heart, MapPin, Briefcase, TrendingUp, AlertTriangle, GraduationCap, CreditCard, Home, Baby, Trash2, Lock, Clipboard, FileText, DollarSign, Award, Printer, Paperclip, ShieldCheck, CheckCircle, XCircle, Phone, RefreshCw, X, Shield, Copy, Unlock, LayoutDashboard, History, FolderOpen, Wallet, KeyRound, FileDown, ChevronRight, ArrowRight, Plus, ArrowRightLeft, FileSignature, PauseCircle, PlayCircle, LogOut, UserRound, BadgePlus } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import PhotoProfil from '../components/PhotoProfil';
import LienFichierSecurise from '../components/LienFichierSecurise';
import DossierSalarialTab from '../components/DossierSalarialTab';
import CategorieSelector, { chargerCategories } from '../components/CategorieSelector';
import PosteSelector from '../components/PosteSelector';
import BandeauSalarie from '../components/employes/BandeauSalarie';
import ApercuSalarie from '../components/employes/ApercuSalarie';
import DocumentsSalarie from '../components/employes/DocumentsSalarie';
import EmploiSalarie from '../components/employes/EmploiSalarie';
import HistoriqueSalarie from '../components/employes/HistoriqueSalarie';
import { ModaleContrat, ModaleSortie, ModaleStatut, ModaleResponsable, ModaleMetier, ModaleAnnulation } from '../components/employes/ActionsSalarie';
import { EnTetePage, Onglets, BandeauInfo, Squelette, MenuActions } from '../design/composants';
import { FAMILLES_PROFESSIONNELLES, humaniserFamilleProfessionnelle } from '../constants/libellesHumains';

const VERT = '#1B5E20';
const VERT_CLAIR = '#E8F5E9';
const ROUGE = '#C62828';
const GRIS = '#F8F9FA';
const GRIS_BORD = '#E0E0E0';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '–';

const RO_STYLE = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e0e0e0', background: '#f5f5f5', color: '#888', cursor: 'not-allowed', fontSize: 13, boxSizing: 'border-box' };

// Module 1 (D-E9) : primitives de la fiche alignées sur le socle visuel commun (design/sogas-ui.css).
const Badge = ({ texte, couleur = 'vert' }) => {
  const ton = { vert: 'succes', rouge: 'danger', bleu: 'info', gris: 'neutre' }[couleur] || 'neutre';
  return <span className={`rh-badge rh-ton-${ton}`}>{texte}</span>;
};

const Card = ({ children, style = {} }) => (
  <section className="rh-carte" style={style}>{children}</section>
);

const CardTitre = ({ Ico, titre, action }) => (
  <div className="rh-carte-entete">
    <h2 className="rh-carte-titre">{Ico && <Ico size={17} aria-hidden="true" />}{titre}</h2>
    {action && action}
  </div>
);

const BtnEdit = ({ onClick, libelle = 'Corriger' }) => (
  <button type="button" onClick={onClick} className="rh-btn rh-btn-petit" title="Corriger une erreur de saisie">
    <Pencil size={13} aria-hidden="true" />{libelle}
  </button>
);

const BtnSave = ({ onClick, loading }) => (
  <button type="button" onClick={onClick} disabled={loading} className="rh-btn rh-btn-petit rh-btn-primaire">
    <Save size={13} aria-hidden="true" />{loading ? 'Enregistrement…' : 'Enregistrer'}
  </button>
);

const BtnCancel = ({ onClick }) => (
  <button type="button" onClick={onClick} className="rh-btn rh-btn-petit">
    <X size={13} aria-hidden="true" />Annuler
  </button>
);

const BtnAjouter = ({ onClick, disabled, libelle = 'Ajouter' }) => (
  <button type="button" onClick={onClick} disabled={disabled} className="rh-btn rh-btn-petit rh-btn-lien">
    <Plus size={13} aria-hidden="true" />{libelle}
  </button>
);

const Champ = ({ label, valeur, edit, input }) => {
  const vide = !valeur || valeur === '' || valeur === '–';
  return (
    <div className="rh-champ">
      <div className="rh-champ-libelle">{label}</div>
      {edit ? input : <div className={`rh-champ-valeur ${vide ? 'rh-champ-vide' : ''}`}>{vide ? 'Non renseigné' : valeur}</div>}
    </div>
  );
};

const Grille = ({ children }) => <div className="rh-grille">{children}</div>;

const InputEdit = ({ value, onChange, type = 'text', options }) => {
  if (options) return (
    <select value={value || ''} onChange={onChange} className="rh-champ-saisie">
      {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
  return <input type={type} value={value || ''} onChange={onChange} className="rh-champ-saisie" />;
};

const Modal = ({ titre, onClose, children }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} role="dialog" aria-modal="true" aria-label={titre}>
    <div className="rh-carte" style={{ width: 520, maxWidth: '100%', maxHeight: '88vh', overflowY: 'auto', boxShadow: 'var(--sg-ombre-haute)', marginBottom: 0, fontFamily: 'var(--sg-police)' }}>
      <div className="rh-carte-entete">
        <h2 className="rh-carte-titre">{titre}</h2>
        <button type="button" onClick={onClose} className="rh-btn rh-btn-discret rh-btn-icone" aria-label="Fermer"><X size={18} /></button>
      </div>
      {children}
    </div>
  </div>
);

const ConfirmModal = ({ action, onClose }) => {
  if (!action?.visible) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} role="alertdialog" aria-modal="true" aria-label={action.titre}>
      <div className="rh-carte" style={{ width: 400, maxWidth: '100%', boxShadow: 'var(--sg-ombre-haute)', marginBottom: 0, fontFamily: 'var(--sg-police)' }}>
        <h2 className="rh-carte-titre" style={{ marginBottom: 10 }}>{action.titre}</h2>
        <p style={{ margin: '0 0 20px', fontSize: 13, color: 'var(--sg-texte-2)', lineHeight: 1.5 }}>{action.message}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} className="rh-btn">Annuler</button>
          <button type="button" onClick={() => { action.onConfirm(); onClose(); }} className="rh-btn rh-btn-primaire" style={{ background: action.couleurBtn || 'var(--sg-danger)', borderColor: action.couleurBtn || 'var(--sg-danger)' }}>{action.btnLabel || 'Confirmer'}</button>
        </div>
      </div>
    </div>
  );
};

const InputModal = ({ label, value, onChange, type = 'text', required }) => (
  <label style={{ display: 'block', marginBottom: 14 }}>
    <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>
      {label}{required && <span style={{ color: 'var(--sg-danger)' }}> *</span>}
    </span>
    <input type={type} value={value || ''} onChange={onChange} className="rh-champ-saisie" />
  </label>
);

const SelectModal = ({ label, value, onChange, options }) => (
  <label style={{ display: 'block', marginBottom: 14 }}>
    <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>{label}</span>
    <select value={value} onChange={onChange} className="rh-champ-saisie">
      {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  </label>
);

// ─── Justificatif d'une épouse ou d'un enfant (décision du 3 octobre 2026) ───
// Sans pièce vérifiée par un RH, la personne n'ouvre aucun droit (IPM, et à terme paie et allocations).
function Justificatif({ personne, type, peutVerifier, onVerifier }) {
  const j = personne.justificatif || {};
  const piece = type === 'conjoint' ? personne.certificat_url : personne.acte_naissance_url;
  const nomPiece = type === 'conjoint' ? 'Certificat de mariage' : 'Acte de naissance';
  const ton = j.etat === 'verifie' ? 'succes' : j.en_delai ? 'alerte' : 'danger';
  const libelle = j.etat === 'verifie' ? `${nomPiece} vérifié` : j.etat === 'a_verifier' ? `${nomPiece} à vérifier` : `${nomPiece} manquant`;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
      <span className={`rh-etat rh-etat-${ton}`} title={j.motif || undefined}><span className="rh-badge-point" aria-hidden="true" />{libelle}</span>
      {piece && <LienFichierSecurise chemin={piece} style={{ fontSize: 12, color: 'var(--sg-primaire)' }}>Voir la pièce</LienFichierSecurise>}
      {j.etat === 'a_verifier' && peutVerifier && (
        <button type="button" className="rh-btn rh-btn-petit rh-btn-lien" onClick={onVerifier}><ShieldCheck size={13} aria-hidden="true" />Marquer comme vérifiée</button>
      )}
      <span style={{ fontSize: 12, color: 'var(--sg-texte-3)', width: '100%' }}>
        {j.etat === 'verifie'
          ? `Vérifiée${j.verifie_par_nom ? ` par ${j.verifie_par_nom}` : ''}${j.verifie_le ? ` le ${new Date(j.verifie_le).toLocaleDateString('fr-FR')}` : ''} — droits ouverts`
          : j.en_delai ? `Droits maintenus jusqu'au ${j.echeance.split('-').reverse().join('/')} : régulariser avant cette date`
            : 'Aucun droit ouvert (IPM) tant que la pièce n\'est pas déposée et vérifiée par un RH'}
      </span>
    </div>
  );
}

// ─── Composant Section Sécurité & Accès ──────────────────────────────────
function SectionSecurite({ employe, userConnecte, onRefresh }) {
  const API = process.env.REACT_APP_API_URL || '';
  const token = localStorage.getItem('sogas_token');
  const headers = { Authorization: `Bearer ${token}` };
  const [securite, setSecurite] = useState(null);
  const [loadingSec, setLoadingSec] = useState(true);
  const [mdpTemp, setMdpTemp] = useState(null);
  const [modalMdp, setModalMdp] = useState(false);
  const [msgSec, setMsgSec] = useState(null);
  const [copied, setCopied] = useState(false);

  const charger = async () => {
    setLoadingSec(true);
    try {
      const r = await axios.get(`${API}/api/auth/securite/${employe.id}`, { headers });
      setSecurite(r.data);
    } catch { setSecurite(null); }
    setLoadingSec(false);
  };

  useEffect(() => { charger(); }, [employe.id]);

  const reinitialiser = async () => {
    try {
      const r = await axios.post(`${API}/api/auth/reinitialiser/${employe.id}`, {}, { headers });
      setMdpTemp(r.data.mot_de_passe_temporaire);
      setModalMdp(true);
      setMsgSec({ type: 'ok', text: r.data.message || 'Mot de passe réinitialisé avec succès.' });
      charger(); onRefresh && onRefresh();
    } catch (e) {
      setMsgSec({ type: 'err', text: e.response?.data?.message || 'Erreur lors de la réinitialisation' });
    }
  };

  // D-S9 : bloquer l'accès est une décision distincte de la situation professionnelle.
  const [blocage, setBlocage] = useState(null); // null | { motif }
  const bloquer = async () => {
    try {
      const r = await axios.post(`${API}/api/auth/bloquer/${employe.id}`, { motif: blocage.motif }, { headers });
      setBlocage(null);
      setMsgSec({ type: 'ok', text: r.data.message });
      charger(); onRefresh && onRefresh();
    } catch (e) {
      setMsgSec({ type: 'err', text: e.response?.data?.message || 'Erreur' });
    }
  };

  const debloquer = async () => {
    try {
      await axios.post(`${API}/api/auth/debloquer/${employe.id}`, {}, { headers });
      setMsgSec({ type: 'ok', text: 'Accès débloqué.' });
      charger(); onRefresh && onRefresh();
    } catch (e) {
      setMsgSec({ type: 'err', text: e.response?.data?.message || 'Erreur' });
    }
  };

  const copier = () => {
    if (mdpTemp) {
      navigator.clipboard.writeText(mdpTemp);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const fmtDT = (d) => d ? new Date(d).toLocaleString('fr-FR') : '–';

  if (loadingSec) return <Card><div style={{ textAlign:'center', padding:'30px 0', color:'#bbb' }}>Chargement…</div></Card>;

  const s = securite;
  const bloque = s?.compte_bloque;
  const sorti = !['Actif', 'Suspendu'].includes(employe.statut);
  const parDecision = s?.blocage?.origine === 'DECISION_RH';

  return (
    <>
      <Card>
        <CardTitre Ico={Shield} titre="Accès et Sécurité" />

        {msgSec && (
          <div style={{
            padding:'10px 14px', borderRadius:8, marginBottom:14,
            background: msgSec.type==='ok' ? '#E8F5E9' : '#FFEBEE',
            border: `1px solid ${msgSec.type==='ok' ? '#A5D6A7' : '#EF9A9A'}`,
            color: msgSec.type==='ok' ? VERT : ROUGE, fontSize:12, fontWeight:600,
            display:'flex', alignItems:'center', gap:8,
          }}>
            {msgSec.type==='ok' ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
            {msgSec.text}
            <button onClick={()=>setMsgSec(null)} style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer'}}>
              <X size={12} />
            </button>
          </div>
        )}

        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px,1fr))', gap:'12px 24px', marginBottom:20 }}>
          {[
            {
              label: 'Accès à l\'application',
              valeur: sorti
                ? <span style={{color:'var(--sg-texte-3)', fontWeight:700, display:'flex',alignItems:'center',gap:4}}><Lock size={13}/>Refusé (salarié sorti)</span>
                : bloque
                ? <span style={{color:ROUGE, fontWeight:700, display:'flex',alignItems:'center',gap:4}}><Lock size={13}/>{parDecision ? 'Bloqué par décision RH' : 'Bloqué (tentatives échouées)'}</span>
                : <span style={{color:VERT, fontWeight:700, display:'flex',alignItems:'center',gap:4}}><Unlock size={13}/>Autorisé</span>
            },
            {
              label: 'Première connexion',
              valeur: s?.premier_connexion
                ? <span style={{color:'#E65100',fontWeight:700}}>En attente</span>
                : <span style={{color:VERT,fontWeight:700}}>Effectuée</span>
            },
            {
              label: 'Dernière connexion',
              valeur: fmtDT(s?.derniere_connexion),
            },
            {
              label: 'Tentatives échouées',
              valeur: <span style={{color: s?.tentatives_echouees > 0 ? ROUGE : '#555', fontWeight:700}}>
                {s?.tentatives_echouees || 0} / 5
              </span>
            },
            {
              label: 'Dernier changement du mot de passe',
              valeur: s?.date_changement_mdp ? fmtDT(s.date_changement_mdp) : 'Jamais',
            },
            bloque && {
              label: 'Date de blocage',
              valeur: fmtDT(s?.date_blocage),
            },
            bloque && parDecision && {
              label: 'Motif du blocage',
              valeur: s?.blocage?.motif || 'Non renseigné',
            },
          ].filter(Boolean).map((item, i) => (
            <div key={i}>
              <div className="rh-champ-libelle">{item.label}</div>
              <div style={{ fontSize:13, color:'#333' }}>{item.valeur}</div>
            </div>
          ))}
        </div>

        {/* Journaux récents */}
        {s?.derniers_logs?.length > 0 && (
          <div style={{marginBottom:20}}>
            <div className="rh-champ-libelle" style={{ marginBottom: 8 }}>Dernières tentatives de connexion</div>
            <div style={{display:'flex',flexDirection:'column',gap:4}}>
              {s.derniers_logs.map((log, i) => (
                <div key={i} style={{
                  display:'flex', alignItems:'center', gap:10,
                  padding:'7px 12px', borderRadius:6,
                  background: log.statut==='succes' ? '#F1F8E9' : log.statut==='bloque' ? '#3e0000' : '#FFF3E0',
                  border:`1px solid ${log.statut==='succes'?'#C5E1A5':log.statut==='bloque'?'#7f0000':'#FFCC02'}`,
                  fontSize:11,
                }}>
                  {log.statut==='succes'
                    ? <CheckCircle size={12} color={VERT}/>
                    : log.statut==='bloque'
                      ? <Lock size={12} color={ROUGE}/>
                      : <AlertTriangle size={12} color="#E65100"/>}
                  <span style={{fontWeight:600,color:log.statut==='bloque'?'#ffcccc':'#333'}}>
                    {log.statut==='succes'?'Succès':log.statut==='bloque'?'Bloqué':'Échec'}
                  </span>
                  {log.motif && <span style={{color:'#888',flex:1}}>{log.motif}</span>}
                  <span style={{color:'#aaa',marginLeft:'auto'}}>{new Date(log.created_at).toLocaleString('fr-FR')}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
          <button type="button" onClick={reinitialiser} className="rh-btn rh-btn-primaire">
            <RefreshCw size={13}/>Réinitialiser le mot de passe
          </button>
          {bloque && (
            <button type="button" onClick={debloquer} className="rh-btn">
              <Unlock size={13}/>Débloquer l'accès
            </button>
          )}
          {!bloque && userConnecte?.id !== employe.id && ['Actif', 'Suspendu'].includes(employe.statut) && (
            <button type="button" onClick={() => setBlocage({ motif: '' })} className="rh-btn rh-btn-danger">
              <Lock size={13}/>Bloquer l'accès
            </button>
          )}
        </div>
        <p style={{ margin: '12px 0 0', fontSize: 12, color: 'var(--sg-texte-4)' }}>
          L'accès à l'application est distinct de la situation professionnelle : un salarié suspendu garde son accès, sauf blocage décidé ici.
        </p>
        {blocage && (
          <div role="dialog" aria-modal="true" aria-label="Bloquer l'accès" style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div className="rh-carte" style={{ width: 460, maxWidth: '100%', boxShadow: 'var(--sg-ombre-haute)', marginBottom: 0 }}>
              <h2 className="rh-carte-titre" style={{ marginBottom: 8 }}><Lock size={17} aria-hidden="true" />Bloquer l'accès à l'application</h2>
              <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--sg-texte-2)' }}>
                {employe.prenom} {employe.nom} ne pourra plus se connecter, y compris avec une session déjà ouverte. Sa situation professionnelle (statut, contrat, poste) ne change pas. Le blocage est journalisé et se lève par « Débloquer l'accès ».
              </p>
              <label style={{ display: 'block', marginBottom: 14 }}>
                <span style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}>Motif <span style={{ color: 'var(--sg-danger)' }}>*</span></span>
                <input className="rh-champ-saisie" value={blocage.motif} onChange={(e) => setBlocage({ motif: e.target.value })} placeholder="Ex. mise à pied : accès suspendu pendant l'enquête" />
              </label>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button type="button" className="rh-btn" onClick={() => setBlocage(null)}>Annuler</button>
                <button type="button" className="rh-btn rh-btn-primaire" style={{ background: 'var(--sg-danger)', borderColor: 'var(--sg-danger)' }} disabled={blocage.motif.trim().length < 5} onClick={bloquer}>Bloquer l'accès</button>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Modal mot de passe temporaire */}
      {modalMdp && (
        <div style={{
          position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',
          display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:16,
        }}>
          <div style={{
            background:'#fff',borderRadius:14,padding:28,maxWidth:420,width:'100%',
            boxShadow:'0 20px 60px rgba(0,0,0,0.25)',
          }}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:16}}>
              <Shield size={22} color={VERT}/>
              <div style={{fontWeight:800,fontSize:16}}>Mot de passe temporaire généré</div>
              <button onClick={()=>setModalMdp(false)} style={{marginLeft:'auto',background:'none',border:'none',cursor:'pointer'}}>
                <X size={18} color="#999"/>
              </button>
            </div>
            <div style={{fontSize:12,color:'#666',marginBottom:16,lineHeight:1.5}}>
              Communiquez ce mot de passe à <strong>{employe.prenom} {employe.nom}</strong>.
              Il sera invité à le changer dès sa prochaine connexion.
            </div>
            <div style={{
              background:'#F3F3F3',borderRadius:10,padding:'14px 16px',
              display:'flex',alignItems:'center',justifyContent:'space-between',
              border:'1.5px solid #E0E0E0',marginBottom:16,
            }}>
              <span style={{fontFamily:'monospace',fontSize:18,fontWeight:800,letterSpacing:2,color:'#1a1a1a'}}>
                {mdpTemp}
              </span>
              <button onClick={copier} style={{
                background:copied?VERT:'#fff',color:copied?'#fff':VERT,
                border:`1.5px solid ${VERT}`,borderRadius:6,padding:'6px 12px',
                fontSize:12,fontWeight:700,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:5,
                transition:'all 0.2s',
              }}>
                <Copy size={13}/>{copied?'Copié !':'Copier'}
              </button>
            </div>
            <button onClick={()=>setModalMdp(false)} style={{
              width:'100%',padding:'10px 0',background:VERT,color:'#fff',
              border:'none',borderRadius:8,fontWeight:700,cursor:'pointer',fontSize:13,
            }}>Fermer</button>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Modal Dossier Numérique ─────────────────────────────────────────────────
// Défini HORS du composant principal (react-component-rules)
function ModalDossier({ dossier, loading, onClose, onDownloadPDF, loadingPDF }) {
  if (!dossier && !loading) return null;
  const fmtD = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '–';

  const Section = ({ titre, count, items }) => (
    <div style={{ marginBottom: 16, padding: '14px 16px', borderRadius: 10, background: '#F8F9FA', border: '1px solid #E0E0E0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: '#1B5E20' }}>{titre}</span>
        <span style={{ background: '#C8E6C9', color: '#1B5E20', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700 }}>{count}</span>
      </div>
      {items.length > 0 ? items.map((item, i) => (
        <div key={i} style={{ fontSize: 11, color: '#555', padding: '4px 0', borderTop: i > 0 ? '1px solid #EEE' : 'none' }}>{item}</div>
      )) : <div style={{ fontSize: 11, color: '#bbb' }}>Aucun enregistrement</div>}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 640, maxHeight: '88vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        {/* En-tête */}
        <div style={{ background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)', borderRadius: '16px 16px 0 0', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Dossier administratif</div>
            <div style={{ color: '#fff', fontWeight: 800, fontSize: 17 }}>{dossier?.numero_dossier || '…'}</div>
            <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11, marginTop: 2 }}>
              {dossier?.employe ? `${dossier.employe.prenom} ${dossier.employe.nom} — ${dossier.employe.matricule}` : ''}
            </div>
          </div>
          <button onClick={onClose} aria-label="Fermer" style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: '#fff', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={18} /></button>
        </div>

        <div style={{ padding: '20px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#bbb' }}>
              <div style={{ fontSize: 13 }}>Chargement du dossier…</div>
            </div>
          ) : dossier ? (
            <>
              {/* Aperçu sections */}
              <Section
                titre="Identité et Contrat"
                count={1}
                items={[
                  `${dossier.employe?.poste || '–'} — ${siteEmploye(dossier.employe)}`,
                  `Embauché le ${fmtD(dossier.employe?.date_embauche)} — ${dossier.employe?.type_contrat || '–'}`,
                  `Famille : ${dossier.famille?.epouses?.length || 0} épouse(s), ${dossier.famille?.enfants?.length || 0} enfant(s)`,
                ]}
              />
              <Section
                titre="Carrière"
                count={`${dossier.carriere?.affectations?.length || 0} affectation(s) · ${dossier.carriere?.augmentations?.length || 0} augmentation(s)`}
                items={(dossier.carriere?.affectations || []).slice(0, 3).map(a => `${fmtD(a.date_affectation)} — ${a.nouveau_poste || a.nouveau_site || '–'}`)}
              />
              <Section
                titre="Discipline"
                count={`${dossier.discipline?.total || 0} sanction(s) · ${dossier.discipline?.actives || 0} active(s)`}
                items={(dossier.discipline?.sanctions || []).slice(0, 3).map(s => `${fmtD(s.date_sanction)} — ${s.type_sanction} (${s.statut})`)}
              />
              <Section
                titre="Formation"
                count={`${dossier.formations?.total || 0} formation(s)`}
                items={(dossier.formations?.liste || []).slice(0, 3).map(f => `${f.titre} — ${f.statut}`)}
              />
              <Section
                titre="Prêts SOGAS"
                count={`${dossier.prets?.total || 0} prêt(s)`}
                items={(dossier.prets?.liste || []).slice(0, 3).map(p => `${p.numero_dossier || '–'} — ${p.statut}`)}
              />
              <Section
                titre="Documents archivés"
                count={`${dossier.documents?.total || 0} document(s)`}
                items={Object.entries(dossier.documents?.parCategorie || {}).map(([cat, docs]) => `${cat} : ${docs.length} fichier(s)`)}
              />

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button onClick={onDownloadPDF} disabled={loadingPDF}
                  style={{ flex: 1, background: '#1B5E20', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 0', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: loadingPDF ? 0.6 : 1 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  {loadingPDF ? 'Génération du PDF…' : 'Télécharger PDF complet'}
                </button>
                <button onClick={onClose}
                  style={{ background: '#fff', color: '#555', border: '1px solid #ddd', borderRadius: 8, padding: '12px 20px', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}>
                  Fermer
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const BtnsModal = ({ onCancel, onSave, disabled = false }) => (
  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
    <button onClick={onCancel} style={{ background: 'none', border: '1px solid #ddd', borderRadius: 6, padding: '8px 18px', cursor: 'pointer', fontSize: 13 }}>Annuler</button>
    <button onClick={onSave} disabled={disabled} style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5, opacity: disabled ? 0.5 : 1 }}><Save size={13} />Enregistrer</button>
  </div>
);

export default function EmployeProfilPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { employe: userConnecte, hasPermission } = useAuth();
  // Espace salarié historique (« Mon espace », « Ma carte ») : uniquement un salarié de rôle
  // « employé » sur SA propre fiche. Un salarié de rôle « employé » qui encadre une équipe (poste
  // habilitant) ouvre la fiche d'un collaborateur dans la fiche de référence, comme tout manager.
  const estEmployeRole = userConnecte?.role === 'employe' && userConnecte?.id === id;

  // Corrections de la fiche : mêmes rôles que la route PUT /api/employes/:id (avant : tout rôle
  // non « employé » voyait les boutons, et le serveur refusait ensuite).
  const peutModifier = ['rh_principal', 'assistant_rh', 'drh'].includes(userConnecte?.role);

  // RH-PAIE-2I §4/§11 — les actions de carrière (affectation/augmentation/promotion) ont leur
  // propre permission dédiée côté backend (carriereController.js), distincte du flag générique
  // `peutModifier` ci-dessus (qui autorisait à tort TOUT rôle non-employé — ex. chef_service,
  // responsable_site — à voir ces boutons alors que le backend les aurait refusés avec un 403).
  // Miroir exact de carriereController.ROLES_CREATION_CARRIERE / ROLES_SUPPRESSION.
  const ROLES_CREATION_CARRIERE = ['drh', 'rh_principal', 'assistant_rh'];
  const ROLES_SUPPRESSION_CARRIERE = ['drh', 'rh_principal'];
  const peutCreerAffectation = ROLES_CREATION_CARRIERE.includes(userConnecte?.role) || hasPermission('peut_creer_affectation');
  const peutCreerAugmentation = ROLES_CREATION_CARRIERE.includes(userConnecte?.role) || hasPermission('peut_creer_augmentation');
  const peutCreerPromotion = ROLES_CREATION_CARRIERE.includes(userConnecte?.role) || hasPermission('peut_creer_promotion');
  const peutSupprimerCarriere = ROLES_SUPPRESSION_CARRIERE.includes(userConnecte?.role);

  const [employe, setEmploye] = useState(null);
  const [datePrevisionRetraite, setDatePrevisionRetraite] = useState(null);
  // RH-PAIE-2F §8 — famille professionnelle connue via le dossier salarial actif, affichée
  // partout où la catégorie l'est (une catégorie seule est ambiguë entre plusieurs familles).
  const [familleProfessionnelle, setFamilleProfessionnelle] = useState(null);
  const [loading, setLoading] = useState(true);
  // Ouvrir l'onglet Carte (8) si ?tab=carte, Dossier salarial (11) si ?tab=dossier-salarial,
  // sinon Mon Espace (9) pour employe, sinon Identité (1)
  const tabParam = searchParams.get('tab');
  const roleInitial = userConnecte?.role;
  const ongletInitial = tabParam === 'carte' ? 8
    : tabParam === 'dossier-salarial' ? 11
    : (roleInitial === 'employe' && userConnecte?.id === id ? 9 : 1);
  const [onglet, setOnglet] = useState(ongletInitial);

  // Module 1 (D-E2) — sept onglets par usage, visibles selon les droits (le serveur les fixe).
  // L'onglet est porté par l'URL (?onglet=…) : retour navigateur et rechargement le conservent.
  const ONGLET_HERITE = { carte: 'acces', 'dossier-salarial': 'paie' };
  const ongletDemande = searchParams.get('onglet') || ONGLET_HERITE[tabParam] || 'apercu';
  const [synthese, setSynthese] = useState(null);
  // Module 1 (livraison 2) : action métier ouverte, annulation demandée, rafraîchissement des onglets.
  const [actionOuverte, setActionOuverte] = useState(null);
  const [annulationDemandee, setAnnulationDemandee] = useState(null);
  const [rafraichir, setRafraichir] = useState(0);
  const [motifs, setMotifs] = useState({});
  useEffect(() => { axios.get('/api/salaries/motifs').then(({ data }) => setMotifs(data)).catch(() => {}); }, []);
  const [refusAcces, setRefusAcces] = useState('');
  const ongletsAutorises = synthese?.confidentialite?.onglets || [];
  const ongletActif = ongletsAutorises.includes(ongletDemande) ? ongletDemande : 'apercu';
  const voitSensible = !!synthese?.confidentialite?.niveaux?.includes('sensible');
  const changerOnglet = (cle) => {
    const p = new URLSearchParams(searchParams);
    p.set('onglet', cle);
    p.delete('tab');
    setSearchParams(p, { replace: true });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  // Sections historiques de la fiche rangées dans les nouveaux onglets.
  const SECTIONS_ONGLET = { apercu: [], emploi: [4], historique: [5, 6, 7], personnel: [1, 3, 2], documents: [], paie: [11], acces: [10, 8] };
  const voir = (n) => (estEmployeRole ? onglet === n : (SECTIONS_ONGLET[ongletActif] || []).includes(n));
  const [saving, setSaving] = useState(false);
  // RH-PAIE-2C §17 — navigation des onglets : la barre défile déjà horizontalement, mais rien
  // n'indique visuellement qu'il y a plus d'onglets hors champ. Ajout de flèches de défilement
  // conditionnelles + dégradés de bord, sans réécrire la fiche employé.
  const ongletsRef = useRef(null);
  const [ongletsDebordeGauche, setOngletsDebordeGauche] = useState(false);
  const [ongletsDebordeDroite, setOngletsDebordeDroite] = useState(false);
  const majDebordementOnglets = useCallback(() => {
    const el = ongletsRef.current;
    if (!el) return;
    setOngletsDebordeGauche(el.scrollLeft > 4);
    setOngletsDebordeDroite(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);
  useEffect(() => {
    majDebordementOnglets();
    window.addEventListener('resize', majDebordementOnglets);
    return () => window.removeEventListener('resize', majDebordementOnglets);
  }, [majDebordementOnglets, onglet]);
  const defilerOnglets = (sens) => {
    ongletsRef.current?.scrollBy({ left: sens * 160, behavior: 'smooth' });
  };
  const [refSites, setRefSites] = useState([]);
  const [refDeps, setRefDeps] = useState([]);
  const [refSrvs, setRefSrvs] = useState([]);
  const [msg, setMsg] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [editOnglet, setEditOnglet] = useState(null);
  const [formEdit, setFormEdit] = useState({});

  const [epouses, setEpouses] = useState([]);
  const [enfants, setEnfants] = useState([]);
  const [modalEpouse, setModalEpouse] = useState(false);
  const [modalEnfant, setModalEnfant] = useState(false);
  const [epouseEdit, setEpouseEdit] = useState(null);
  const [enfantEdit, setEnfantEdit] = useState(null);
  const [formEpouse, setFormEpouse] = useState({ nom: '', prenom: '', date_naissance: '', date_mariage: '', numero_certificat: '', statut: 'Mariée' });
  const [formEnfant, setFormEnfant] = useState({ prenom: '', nom: '', date_naissance: '', sexe: '', lien: 'Enfant' });

  const [affectations, setAffectations] = useState([]);
  const [augmentations, setAugmentations] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [modalAffectation, setModalAffectation] = useState(false);
  const [modalAugmentation, setModalAugmentation] = useState(false);
  const [modalPromotion, setModalPromotion] = useState(false);
  const [affectationEdit, setAffectationEdit] = useState(null);
  // Module 0 (clôture) : annulation tracée d'un mouvement (motif obligatoire, aperçu du retour)
  const [annulation, setAnnulation] = useState(null);
  const [responsableSource, setResponsableSource] = useState('FICHE');
  const [augmentationEdit, setAugmentationEdit] = useState(null);
  const [promotionEdit, setPromotionEdit] = useState(null);
  // Module 0 : le nouveau rattachement est transmis par IDENTIFIANTS (nouveau_*_id) ; les
  // libellés ancien_* / nouveau_* ne servent qu'à l'affichage (historique figé côté serveur).
  const videAffectation = { date_affectation: '', ancien_site: '', nouveau_site: '', nouveau_site_id: '', ancien_poste: '', nouveau_poste: '', nouveau_poste_id: '', ancien_departement: '', nouveau_departement: '', nouveau_departement_id: '', ancien_service: '', nouveau_service: '', nouveau_service_id: '', motif: '', fichier: null };
  const videAugmentation = { date_augmentation: '', nature: '', nouveau_sursalaire: '', motif: '', fichier: null };
  const videPromotion = { date_promotion: '', nouveau_titre: '', nouveau_poste_id: '', nouvelle_famille_professionnelle: '', nouvelle_categorie: '', nouvel_echelon: '', nouveau_statut_cadre: '', motif: '', fichier: null, confirmationPosteSeul: false };
  const [formAffectation, setFormAffectation] = useState(videAffectation);
  const [formAugmentation, setFormAugmentation] = useState(videAugmentation);
  const [formPromotion, setFormPromotion] = useState(videPromotion);
  // RH-PAIE-2I §9B — catégories (avec leurs échelons) pour peupler le sélecteur d'échelon de la
  // promotion, et salaire catégoriel proposé résolu depuis la grille active à la date choisie.
  const [categoriesPourPromotion, setCategoriesPourPromotion] = useState([]);
  const [salaireBaseProposePromotion, setSalaireBaseProposePromotion] = useState(undefined);
  useEffect(() => { chargerCategories().then(setCategoriesPourPromotion); }, []);
  // RH-PAIE-2I §9B — résolution live du salaire de base proposé, même principe que
  // DossierSalarialTab.jsx : jamais une valeur devinée, toujours résolue depuis la grille
  // active à la date de promotion choisie.
  useEffect(() => {
    if (!modalPromotion || !formPromotion.nouvelle_famille_professionnelle || !formPromotion.nouvelle_categorie || !formPromotion.date_promotion) {
      setSalaireBaseProposePromotion(undefined);
      return;
    }
    let annule = false;
    axios.get(`/api/paie/parametres/SALAIRE_CATEGORIEL_BASE/resoudre`, {
      params: { date_reference: formPromotion.date_promotion, famille_professionnelle: formPromotion.nouvelle_famille_professionnelle, categorie: formPromotion.nouvelle_categorie, echelon: formPromotion.nouvel_echelon || undefined },
    }).then(({ data }) => { if (!annule) setSalaireBaseProposePromotion(data.data ? Number(data.data.valeur_numerique) : null); })
      .catch(() => { if (!annule) setSalaireBaseProposePromotion(null); });
    return () => { annule = true; };
  }, [modalPromotion, formPromotion.nouvelle_famille_professionnelle, formPromotion.nouvelle_categorie, formPromotion.nouvel_echelon, formPromotion.date_promotion]);

  const [posteHierarchique, setPosteHierarchique] = useState(null);
  const [tousPostes, setTousPostes] = useState([]);
  const [loadingPoste, setLoadingPoste] = useState(false);
  const [managerInfo, setManagerInfo] = useState(null);
  const [subordonnes, setSubordonnes] = useState([]);
  const [posteOrg, setPosteOrg] = useState(null);
  const [posteManagerInfo, setPosteManagerInfo] = useState(null);
  const [savingManager, setSavingManager] = useState(false);
  // Le rattachement s'affiche d'abord en lecture ; il ne se modifie qu'après « Corriger ».
  const [editionHierarchie, setEditionHierarchie] = useState(false);
  const [formManager, setFormManager] = useState({ poste_manager_id: '', poste_id: '' });

  // ── Discipline
  const [sanctions, setSanctions] = useState([]);
  const [loadingSanctions, setLoadingSanctions] = useState(false);
  const [modalSanction, setModalSanction] = useState(false);
  const [savingSanction, setSavingSanction] = useState(false);
  const videFormSanction = { type_sanction: 'Avertissement', motif: '', date_sanction: '', duree_jours: '', fichier: null };
  const [formSanction, setFormSanction] = useState(videFormSanction);

  // ── Formation
  const [formations, setFormations] = useState([]);
  const [loadingFormations, setLoadingFormations] = useState(false);
  const [modalFormation, setModalFormation] = useState(false);
  const [savingFormation, setSavingFormation] = useState(false);
  const videFormFormation = { titre: '', organisme: '', date_debut: '', date_fin: '', type_formation: 'externe', statut: 'planifiee', cout: '', fichier: null };
  const [formFormation, setFormFormation] = useState(videFormFormation);

  // ── Dossier numérique
  const [modalDossier, setModalDossier] = useState(false);
  const [dossier, setDossier] = useState(null);
  const [loadingDossier, setLoadingDossier] = useState(false);
  const [loadingPDF, setLoadingPDF] = useState(false);

  // Passage d'une fiche à une autre (responsable, collaborateur) : squelette plutôt que l'ancien dossier.
  useEffect(() => { setSynthese(null); chargerEmploye(); }, [id]);
  useEffect(() => { if (voir(2) && voitSensible) { chargerEpouses(); chargerEnfants(); } }, [onglet, ongletActif, voitSensible]);
  useEffect(() => { if (voir(4) && employe) chargerPosteHierarchique(); }, [onglet, ongletActif, employe?.id]);
  useEffect(() => { if (voir(5)) { chargerAffectations(); chargerAugmentations(); chargerPromotions(); } }, [onglet, ongletActif]);
  useEffect(() => { if (voir(6) && voitSensible) chargerSanctions(); }, [onglet, ongletActif, voitSensible]);
  useEffect(() => { if (voir(7) && voitSensible) chargerFormations(); }, [onglet, ongletActif, voitSensible]);
  // Onglet Mon Espace (9) — charger les données carrière pour l'employé connecté
  useEffect(() => { if (onglet === 9) { chargerAffectations(); chargerAugmentations(); chargerPromotions(); } }, [onglet]);

  // ── QR Code Ma Carte (employé uniquement — TOTP depuis backend)
  const [qrImageData, setQrImageData] = useState(null);
  useEffect(() => {
    if (onglet !== 8 || !employe || userConnecte?.role !== 'employe') return;
    const tok = localStorage.getItem('sogas_token');
    axios.get('/api/pointages/mon-qrcode', { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => setQrImageData(r.data.qrImage || null))
      .catch(() => setQrImageData(null));
  }, [onglet, employe?.id, userConnecte?.role]);

  const chargerReferentiels = async () => {
    try {
      const [s, d, sv] = await Promise.all([
        axios.get(`/api/referentiel/sites`),
        axios.get(`/api/referentiel/departements`),
        axios.get(`/api/referentiel/services`),
      ]);
      setRefSites(s.data.sites || []);
      setRefDeps(d.data.departements || []);
      setRefSrvs(sv.data.services || []);
    } catch (e) { console.error(e); }
  };

  const chargerEmploye = async () => {
    // Module 1 : bandeau, onglets autorisés et état du dossier (GET /api/salaries/:id/synthese).
    if (!estEmployeRole) {
      try {
        const { data: syn } = await axios.get(`/api/salaries/${id}/synthese`);
        setSynthese(syn);
        setRefusAcces('');
      } catch (e) {
        const st = e.response?.status;
        setRefusAcces(st === 403 ? (e.response.data?.message || 'Ce dossier ne vous est pas accessible.')
          : st === 404 ? 'Ce salarié est introuvable.' : 'Le dossier n\'a pas pu être chargé. Réessayez dans un instant.');
      }
    }
    try {
      const { data } = await axios.get(`/api/employes/${id}`);
      const emp = data.employe || data;
      setEmploye(emp);
      if (data.manager !== undefined) setManagerInfo(data.manager);
      if (data.subordonnes !== undefined) setSubordonnes(data.subordonnes);
      if (data.posteOrg !== undefined) setPosteOrg(data.posteOrg);
      if (data.posteManager !== undefined) setPosteManagerInfo(data.posteManager);
      if (data.responsable_source) setResponsableSource(data.responsable_source);
      if (data.date_prevision_retraite !== undefined) setDatePrevisionRetraite(data.date_prevision_retraite);
      setRafraichir((x) => x + 1); // frise, contrat : relus après chaque enregistrement
      setFormManager({ poste_manager_id: emp.poste_manager_id || '', poste_id: emp.poste_id || '' });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
    // Dégradation silencieuse si l'utilisateur n'a pas le droit de lire le dossier salarial.
    try {
      const { data } = await axios.get(`/api/employes/${id}/dossier-salarial`);
      setFamilleProfessionnelle(data?.data?.applicable?.famille_professionnelle || null);
    } catch (e) { setFamilleProfessionnelle(null); }
  };

  const chargerPosteHierarchique = async () => {
    setLoadingPoste(true);
    try {
      const rPostes = await axios.get('/api/postes');
      const postes = rPostes.data.postes || [];
      setTousPostes(postes);
      const trouve = postes.find(p => p.titulaire_id === employe?.id) || null;
      setPosteHierarchique(trouve);
    } catch (e) { console.error('chargerPosteHierarchique:', e); }
    finally { setLoadingPoste(false); }
  };

  const sauvegarderManager = async () => {
    setSavingManager(true);
    try {
      // Module 0 : pour un titulaire de poste, le responsable découle de l'organigramme —
      // seul le poste est envoyé ; pour un salarié sans poste, le responsable se saisit ici.
      const corps = { poste_id: formManager.poste_id || null };
      if (!formManager.poste_id) corps.poste_manager_id = formManager.poste_manager_id || null;
      await axios.put(`/api/employes/${id}`, corps);
      await chargerEmploye();
      setEditionHierarchie(false);
      showMsg('Responsable et poste mis à jour');
    } catch (e) {
      showMsg(e.response?.data?.message || 'Erreur lors de la sauvegarde', 'err');
    } finally {
      setSavingManager(false);
    }
  };

  const chargerEpouses = async () => {
    try { const { data } = await axios.get(`/api/employes/${id}/epouses`); setEpouses(data.epouses); } catch (e) {}
  };

  const chargerEnfants = async () => {
    try { const { data } = await axios.get(`/api/employes/${id}/enfants`); setEnfants(data.enfants); } catch (e) {}
  };

  const actionTerminee = () => { setActionOuverte(null); setAnnulationDemandee(null); chargerEmploye(); setRafraichir((x) => x + 1); };
  const showMsg = (texte, type = 'ok') => {
    setMsg({ texte, type });
    setTimeout(() => setMsg(null), 3000);
  };

  const demarrerEdit = (num, champs) => {
    if (refSites.length === 0) chargerReferentiels();
    setEditOnglet(num);
    setFormEdit({ ...champs });
  };
  const annulerEdit = () => { setEditOnglet(null); setFormEdit({}); };

  const sauvegarderEdit = async (body) => {
    setSaving(true);
    try {
      await axios.put(`/api/employes/${id}`, body);
      await chargerEmploye();
      setEditOnglet(null);
      showMsg('Modifications enregistrées');
    } catch (e) {
      showMsg((e.response?.data?.message || 'Erreur serveur'), 'err');
    } finally { setSaving(false); }
  };

  // Décision du 3 octobre 2026 : certificat de mariage et acte de naissance obligatoires (le
  // serveur le vérifie aussi) ; une nouvelle pièce doit être vérifiée par un RH pour ouvrir les droits.
  const soumettreEpouse = async () => {
    if (!epouseEdit && !formEpouse.fichier_certificat) { showMsg('Le certificat de mariage (PDF ou image) est obligatoire pour enregistrer une épouse.', 'err'); return; }
    try {
      if (formEpouse.fichier_certificat) {
        const fd = new FormData();
        Object.keys(formEpouse).forEach(k => { if (k !== 'fichier_certificat' && formEpouse[k]) fd.append(k, formEpouse[k]); });
        fd.append('fichier', formEpouse.fichier_certificat);
        if (epouseEdit) await axios.put(`/api/employes/${id}/epouses/${epouseEdit.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        else await axios.post(`/api/employes/${id}/epouses`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      } else {
        if (epouseEdit) await axios.put(`/api/employes/${id}/epouses/${epouseEdit.id}`, formEpouse);
        else await axios.post(`/api/employes/${id}/epouses`, formEpouse);
      }
      setModalEpouse(false); setEpouseEdit(null);
      setFormEpouse({ nom: '', prenom: '', date_naissance: '', date_mariage: '', numero_certificat: '', statut: 'Mariée' });
      chargerEpouses();
      showMsg('Épouse enregistrée');
    } catch (e) { showMsg((e.response?.data?.message || 'Erreur'), 'err'); }
  };

  const supprimerEpouse = (epouseId, epouse) => {
    const nom = [epouse?.prenom, epouse?.nom].filter(Boolean).join(' ');
    setConfirmAction({
      visible: true,
      titre: 'Supprimer cette épouse ?',
      message: `${nom ? nom + ' — ' : ''}Cette action est irréversible.`,
      couleurBtn: ROUGE,
      btnLabel: 'Supprimer',
      onConfirm: async () => {
        await axios.delete(`/api/employes/${id}/epouses/${epouseId}`);
        chargerEpouses();
      },
    });
  };

  const peutVerifierJustificatif = ['drh', 'rh_principal', 'assistant_rh'].includes(userConnecte?.role);
  const verifierJustificatif = async (table, pid) => {
    try {
      const { data } = await axios.post(`/api/employes/${id}/${table}/${pid}/justificatif/verifier`);
      showMsg(data.message);
      if (table === 'epouses') chargerEpouses(); else chargerEnfants();
    } catch (err) { showMsg(err.response?.data?.message || 'Erreur', 'err'); }
  };

  const editerEpouse = (e) => {
    setEpouseEdit(e);
    setFormEpouse({ nom: e.nom, prenom: e.prenom || '', date_naissance: e.date_naissance || '', date_mariage: e.date_mariage || '', numero_certificat: e.numero_certificat || '', statut: e.statut });
    setModalEpouse(true);
  };

  const soumettreEnfant = async () => {
    if (!['M', 'F'].includes(formEnfant.sexe)) { showMsg('Le sexe de l\'enfant est obligatoire (garçon ou fille).', 'err'); return; }
    if (!enfantEdit && !formEnfant.fichier_acte) { showMsg('L\'acte de naissance (PDF ou image) est obligatoire pour enregistrer un enfant.', 'err'); return; }
    try {
      const fdE = new FormData();
      Object.keys(formEnfant).forEach(k => { if (k !== 'fichier_acte' && formEnfant[k]) fdE.append(k, formEnfant[k]); });
      if (formEnfant.fichier_acte) fdE.append('fichier', formEnfant.fichier_acte);
      const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (enfantEdit) await axios.put(`/api/employes/${id}/enfants/${enfantEdit.id}`, fdE, multipart);
      else await axios.post(`/api/employes/${id}/enfants`, fdE, multipart);
      setModalEnfant(false); setEnfantEdit(null);
      setFormEnfant({ prenom: '', nom: '', date_naissance: '', sexe: '', lien: 'Enfant' });
      chargerEnfants();
      showMsg('Enfant enregistré');
    } catch (e) { showMsg((e.response?.data?.message || 'Erreur'), 'err'); }
  };

  const supprimerEnfant = (enfantId, enfant) => {
    const nom = [enfant?.prenom, enfant?.nom].filter(Boolean).join(' ');
    setConfirmAction({
      visible: true,
      titre: 'Supprimer cet enfant ?',
      message: `${nom ? nom + ' — ' : ''}Cette action est irréversible.`,
      couleurBtn: ROUGE,
      btnLabel: 'Supprimer',
      onConfirm: async () => {
        await axios.delete(`/api/employes/${id}/enfants/${enfantId}`);
        chargerEnfants();
      },
    });
  };

  const editerEnfant = (e) => {
    setEnfantEdit(e);
    setFormEnfant({ prenom: e.prenom, nom: e.nom || '', date_naissance: e.date_naissance || '', sexe: e.sexe || '', lien: e.lien || 'Enfant' });
    setModalEnfant(true);
  };

  // ─── CARRIÈRE ────────────────────────────────────────────────────

  const chargerAffectations = async () => {
    try { const { data } = await axios.get(`/api/employes/${id}/affectations`); setAffectations(data.affectations); } catch (e) {}
  };
  const chargerAugmentations = async () => {
    try { const { data } = await axios.get(`/api/employes/${id}/augmentations`); setAugmentations(data.augmentations); } catch (e) {}
  };
  const chargerPromotions = async () => {
    try { const { data } = await axios.get(`/api/employes/${id}/promotions`); setPromotions(data.promotions); } catch (e) {}
  };

  const buildFD = (form, champsExclus = ['fichier']) => {
    const fd = new FormData();
    Object.keys(form).forEach(k => { if (!champsExclus.includes(k) && form[k] !== null && form[k] !== '') fd.append(k, form[k]); });
    if (form.fichier) fd.append('fichier', form.fichier);
    return fd;
  };

  const soumettreAffectation = async () => {
    try {
      // Seuls les identifiants partent au serveur ; en modification, seuls date / motif / document.
      const champs = affectationEdit
        ? { date_affectation: formAffectation.date_affectation, motif: formAffectation.motif, fichier: formAffectation.fichier, motif_correction: formAffectation.motif_correction }
        : {
            date_affectation: formAffectation.date_affectation, motif: formAffectation.motif, fichier: formAffectation.fichier,
            nouveau_site_id: formAffectation.nouveau_site_id, nouveau_departement_id: formAffectation.nouveau_departement_id,
            nouveau_service_id: formAffectation.nouveau_service_id, nouveau_poste_id: formAffectation.nouveau_poste_id,
          };
      const fd = buildFD(champs);
      // Module 1 : changer de site sans choisir de département (ou de département sans service)
      // vide explicitement l'ancien rattachement, qui appartient à l'ancien site — sinon le
      // serveur refuserait un rattachement incohérent.
      if (!affectationEdit) {
        const siteChange = !!formAffectation.nouveau_site_id && formAffectation.nouveau_site_id !== employe.site_id;
        if (siteChange && !formAffectation.nouveau_departement_id) fd.append('nouveau_departement_id', '');
        if ((siteChange || formAffectation.nouveau_departement_id) && !formAffectation.nouveau_service_id) fd.append('nouveau_service_id', '');
      }
      const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (affectationEdit) await axios.put(`/api/employes/${id}/affectations/${affectationEdit.id}`, fd, cfg);
      else { await axios.post(`/api/employes/${id}/affectations`, fd, cfg); await chargerEmploye(); }
      setModalAffectation(false); setAffectationEdit(null); setFormAffectation(videAffectation);
      chargerAffectations(); showMsg(affectationEdit ? 'Mouvement corrigé (correction journalisée)' : 'Affectation enregistrée');
    } catch (e) { showMsg((e.response?.data?.message || 'Erreur'), 'err'); }
  };
  // Module 0 (clôture) : un mouvement ne se supprime plus. Il s'annule (motif obligatoire) :
  // il reste visible dans l'historique et le salarié retrouve sa situation antérieure.
  const ouvrirAnnulation = async (aff) => {
    try {
      const { data } = await axios.get(`/api/employes/${id}/affectations/${aff.id}/annulation`);
      setAnnulation({ aff, apercu: data, motif: '' });
    } catch (e) { showMsg((e.response?.data?.message || 'Erreur'), 'err'); }
  };
  const confirmerAnnulation = async () => {
    try {
      await axios.post(`/api/employes/${id}/affectations/${annulation.aff.id}/annuler`, { motif: annulation.motif });
      setAnnulation(null);
      await chargerEmploye(); chargerAffectations();
      showMsg('Mouvement annulé — situation antérieure rétablie');
    } catch (e) { showMsg((e.response?.data?.message || 'Erreur'), 'err'); }
  };
  const ouvrirNouvelleAffectation = () => {
    setAffectationEdit(null);
    if (refSites.length === 0) chargerReferentiels();
    setFormAffectation({ ...videAffectation, ancien_site: siteEmploye(employe, ''), ancien_departement: employe.departement || '', ancien_service: employe.service || '', ancien_poste: employe.poste || '' });
    setModalAffectation(true);
  };
  const editerAffectation = (a) => {
    setAffectationEdit(a);
    setFormAffectation({ date_affectation: a.date_affectation || '', ancien_site: a.ancien_site || '', nouveau_site: a.nouveau_site || '', ancien_poste: a.ancien_poste || '', nouveau_poste: a.nouveau_poste || '', nouveau_poste_id: a.nouveau_poste_id || '', ancien_departement: a.ancien_departement || '', nouveau_departement: a.nouveau_departement || '', ancien_service: a.ancien_service || '', nouveau_service: a.nouveau_service || '', motif: a.motif || '', motif_correction: '', fichier: null });
    setModalAffectation(true);
  };

  // RH-PAIE-2C §18/§20 — l'augmentation individuelle n'agit plus que sur le sursalaire (ou une
  // nature assimilée) ; le backend calcule lui-même l'ancien sursalaire et refuse tout
  // changement de catégorie (voir carriereController.ajouterAugmentation).
  const aDossierSalarialInitial = () => (employe?.categorie || employe?.salaire_base != null);
  const soumettreAugmentation = async () => {
    try {
      const fd = buildFD(formAugmentation);
      const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (augmentationEdit) await axios.put(`/api/employes/${id}/augmentations/${augmentationEdit.id}`, fd, cfg);
      else { await axios.post(`/api/employes/${id}/augmentations`, fd, cfg); await chargerEmploye(); }
      setModalAugmentation(false); setAugmentationEdit(null); setFormAugmentation(videAugmentation);
      chargerAugmentations(); showMsg('Augmentation enregistrée');
    } catch (e) { showMsg((e.response?.data?.message || 'Erreur'), 'err'); }
  };
  const supprimerAugmentation = (aid, aug) => {
    const montant = aug?.nouveau_salaire ? Number(aug.nouveau_salaire).toLocaleString('fr-FR') + ' FCFA' : null;
    const detail = [montant, aug?.date_augmentation ? fmtDate(aug.date_augmentation) : null].filter(Boolean).join(' — ');
    setConfirmAction({
      visible: true,
      titre: 'Supprimer cette augmentation ?',
      message: `${detail ? detail + ' — ' : ''}Cette action est irréversible.`,
      couleurBtn: ROUGE,
      btnLabel: 'Supprimer',
      onConfirm: async () => {
        await axios.delete(`/api/employes/${id}/augmentations/${aid}`);
        chargerAugmentations();
      },
    });
  };
  const ouvrirNouvelleAugmentation = () => {
    if (!aDossierSalarialInitial()) {
      showMsg('Créez d\'abord le dossier salarial initial de l\'employé avant d\'enregistrer une augmentation ou une promotion.', 'err');
      return;
    }
    setAugmentationEdit(null);
    setFormAugmentation(videAugmentation);
    setModalAugmentation(true);
  };
  const editerAugmentation = (a) => {
    setAugmentationEdit(a);
    setFormAugmentation({ date_augmentation: a.date_augmentation || '', nature: a.nature || '', nouveau_sursalaire: a.nouveau_salaire || '', motif: a.motif || '', fichier: null });
    setModalAugmentation(true);
  };

  // RH-PAIE-2C §21 — la promotion (changement de catégorie) crée sa version salariale de façon
  // atomique côté backend (carriereController.ajouterPromotion + dossierSalarialService), avec
  // salaire catégoriel résolu depuis la grille — jamais saisi ici.
  const soumettrePromotion = async () => {
    try {
      const fd = buildFD(formPromotion);
      const cfg = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (promotionEdit) await axios.put(`/api/employes/${id}/promotions/${promotionEdit.id}`, fd, cfg);
      else { await axios.post(`/api/employes/${id}/promotions`, fd, cfg); await chargerEmploye(); }
      setModalPromotion(false); setPromotionEdit(null); setFormPromotion(videPromotion);
      chargerPromotions(); showMsg('Promotion enregistrée');
    } catch (e) { showMsg((e.response?.data?.message || 'Erreur'), 'err'); }
  };
  const supprimerPromotion = (aid, prom) => {
    const detail = [prom?.nouveau_titre, prom?.date_promotion ? fmtDate(prom.date_promotion) : null].filter(Boolean).join(' — ');
    setConfirmAction({
      visible: true,
      titre: 'Supprimer cette promotion ?',
      message: `${detail ? detail + ' — ' : ''}Cette action est irréversible.`,
      couleurBtn: ROUGE,
      btnLabel: 'Supprimer',
      onConfirm: async () => {
        await axios.delete(`/api/employes/${id}/promotions/${aid}`);
        chargerPromotions();
      },
    });
  };
  const ouvrirNouvellePromotion = () => {
    if (!aDossierSalarialInitial()) {
      showMsg('Créez d\'abord le dossier salarial initial de l\'employé avant d\'enregistrer une augmentation ou une promotion.', 'err');
      return;
    }
    setPromotionEdit(null);
    setFormPromotion(videPromotion);
    setModalPromotion(true);
  };
  const editerPromotion = (p) => {
    setPromotionEdit(p);
    setFormPromotion({ date_promotion: p.date_promotion || '', nouveau_titre: p.nouveau_titre || '', nouveau_poste_id: p.nouveau_poste_id || '', nouvelle_famille_professionnelle: p.nouvelle_famille_professionnelle || '', nouvelle_categorie: p.nouvelle_categorie || '', motif: p.motif || '', fichier: null });
    setModalPromotion(true);
  };

  // ─── DISCIPLINE ──────────────────────────────────────────────────
  const chargerSanctions = async () => {
    setLoadingSanctions(true);
    try {
      const { data } = await axios.get(`/api/employes/${id}/sanctions`);
      setSanctions(data.sanctions || []);
    } catch (e) { setSanctions([]); }
    setLoadingSanctions(false);
  };

  const soumettresSanction = async () => {
    if (!formSanction.motif || !formSanction.date_sanction) { showMsg('Motif et date obligatoires', 'err'); return; }
    setSavingSanction(true);
    try {
      const fd = new FormData();
      fd.append('type_sanction', formSanction.type_sanction);
      fd.append('motif', formSanction.motif);
      fd.append('date_sanction', formSanction.date_sanction);
      if (formSanction.duree_jours) fd.append('duree_jours', formSanction.duree_jours);
      if (formSanction.fichier) fd.append('document', formSanction.fichier);
      await axios.post(`/api/employes/${id}/sanctions`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setModalSanction(false);
      setFormSanction(videFormSanction);
      chargerSanctions();
      showMsg('Sanction enregistrée');
    } catch (e) { showMsg(e.response?.data?.message || 'Erreur', 'err'); }
    setSavingSanction(false);
  };

  const leverSanctionUI = (sanction_id, sanction) => {
    const detail = [sanction?.type_sanction, sanction?.date_sanction ? fmtDate(sanction.date_sanction) : null].filter(Boolean).join(' du ');
    setConfirmAction({
      visible: true,
      titre: 'Lever cette sanction ?',
      message: `${detail ? detail + ' — ' : ''}Cette action ne peut pas être annulée.`,
      couleurBtn: '#E65100',
      btnLabel: 'Lever la sanction',
      onConfirm: async () => {
        try {
          await axios.put(`/api/employes/${id}/sanctions/${sanction_id}/lever`, {});
          chargerSanctions();
          showMsg('Sanction levée');
        } catch (e) { showMsg('Erreur lors de la levée', 'err'); }
      },
    });
  };

  // ─── FORMATION ───────────────────────────────────────────────────
  const chargerFormations = async () => {
    setLoadingFormations(true);
    try {
      const { data } = await axios.get(`/api/employes/${id}/formations`);
      setFormations(data.formations || []);
    } catch (e) { setFormations([]); }
    setLoadingFormations(false);
  };

  const soumettreFormation = async () => {
    if (!formFormation.titre) { showMsg('Le titre est obligatoire', 'err'); return; }
    setSavingFormation(true);
    try {
      const fd = new FormData();
      fd.append('titre', formFormation.titre);
      if (formFormation.organisme) fd.append('organisme', formFormation.organisme);
      if (formFormation.date_debut) fd.append('date_debut', formFormation.date_debut);
      if (formFormation.date_fin) fd.append('date_fin', formFormation.date_fin);
      fd.append('type_formation', formFormation.type_formation);
      fd.append('statut', formFormation.statut);
      if (formFormation.cout) fd.append('cout', formFormation.cout);
      if (formFormation.fichier) fd.append('certificat', formFormation.fichier);
      await axios.post(`/api/employes/${id}/formations`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setModalFormation(false);
      setFormFormation(videFormFormation);
      chargerFormations();
      showMsg('Formation enregistrée');
    } catch (e) { showMsg(e.response?.data?.message || 'Erreur', 'err'); }
    setSavingFormation(false);
  };

  // ─── DOSSIER NUMÉRIQUE ───────────────────────────────────────────
  const ouvrirDossier = async () => {
    setLoadingDossier(true);
    setModalDossier(true);
    try {
      const token = localStorage.getItem('sogas_token');
      const API = process.env.REACT_APP_API_URL || '';
      const { data } = await axios.get(`${API}/api/employes/${id}/dossier`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDossier(data);
    } catch (e) { showMsg(e.response?.data?.message || 'Erreur chargement dossier', 'err'); setModalDossier(false); }
    setLoadingDossier(false);
  };

  const telechargerDossierPDF = async () => {
    setLoadingPDF(true);
    try {
      const token = localStorage.getItem('sogas_token');
      const API = process.env.REACT_APP_API_URL || '';
      const resp = await axios.get(`${API}/api/employes/${id}/dossier/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const cd = resp.headers['content-disposition'] || '';
      const nomFichier = cd.match(/filename="([^"]+)"/)?.[1] || `SOGAS_DOSSIER_${id}.pdf`;
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.href = url;
      a.download = nomFichier;
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 200);
      showMsg('PDF téléchargé', 'ok');
    } catch (e) { showMsg('Erreur génération PDF', 'err'); }
    setLoadingPDF(false);
  };

  let memoListe = '';
  try { memoListe = sessionStorage.getItem('sogas_liste_salaries') || ''; } catch { /* stockage indisponible */ }
  const lienListe = `/employes${memoListe ? `?${memoListe}` : ''}`;
  const titreListe = ({ prepose_regional: 'Mon site', responsable_site: 'Mon site', chef_departement: 'Mon département', chef_service: 'Mon service', chef_equipe: 'Mon équipe', employe: 'Mon équipe' })[userConnecte?.role] || 'Employés';

  if (!estEmployeRole && refusAcces) {
    return (
      <Layout>
        <div className="rh-page">
          <EnTetePage fil={[{ libelle: titreListe, vers: lienListe }, { libelle: 'Dossier salarié' }]} />
          <BandeauInfo ton="danger" titre="Dossier inaccessible">{refusAcces}</BandeauInfo>
        </div>
      </Layout>
    );
  }
  if (loading || (!estEmployeRole && !synthese)) {
    return (
      <Layout>
        <div className="rh-page" aria-busy="true">
          <Squelette largeur={180} hauteur={12} style={{ marginBottom: 18 }} />
          <div className="rh-carte" style={{ display: 'flex', gap: 20 }}>
            <Squelette largeur={64} hauteur={64} style={{ borderRadius: '50%' }} />
            <div style={{ flex: 1 }}><Squelette largeur="35%" hauteur={20} /><Squelette largeur="55%" style={{ marginTop: 12 }} /><Squelette largeur="25%" style={{ marginTop: 8 }} /></div>
          </div>
        </div>
      </Layout>
    );
  }
  if (!employe) return <Layout><div className="rh-page"><BandeauInfo ton="danger" titre="Salarié introuvable">Ce dossier n'existe pas ou n'est plus accessible.</BandeauInfo></div></Layout>;

  const initiales = [employe.prenom?.[0], employe.nom?.[0]].filter(Boolean).join('').toUpperCase() || '?';
  const estActif = employe.statut === 'Actif';

  const estEmploye = estEmployeRole;

  /* ══════════════════════════════════════════════════════════
     PAGE CARTE ÉPURÉE — employe uniquement, onglet 8
     Rendu standalone : pas de Layout, pas d'onglets, pas de header
  ══════════════════════════════════════════════════════════ */
  if (estEmploye && onglet === 8) {
    const annee = new Date().getFullYear();
    const infosSub = [employe.categorie && `${familleProfessionnelle ? humaniserFamilleProfessionnelle(familleProfessionnelle) : 'À classer'} — Cat. ${employe.categorie}`, employe.departement, employe.site && libelleSite(employe.site)].filter(Boolean).join(' · ');

    return (
      <div style={{
        minHeight: '100vh', background: '#f7f7f7',
        display: 'flex', flexDirection: 'column',
        maxWidth: 480, margin: '0 auto',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}>

        {/* ── Retour ── */}
        <div style={{ padding: '14px 16px 0 16px', display: 'flex', alignItems: 'center' }}>
          <button
            onClick={() => navigate('/accueil-employe')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px 0' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M19 12H5M5 12l7 7M5 12l7-7" stroke="#1B5E20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ color: '#1B5E20', fontSize: '0.82rem', fontWeight: 600 }}>Retour</span>
          </button>
        </div>

        {/* ── Label discret ── */}
        <p style={{ textAlign: 'center', padding: '10px 0 4px 0', fontSize: '0.62rem', color: '#bbb', textTransform: 'uppercase', letterSpacing: '1.5px', margin: 0 }}>
          Carte d'employé · Recto / Verso
        </p>

        {/* ── Cartes ── */}
        <div style={{ padding: '0 16px 20px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* RECTO */}
          <div style={{
            background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 100%)',
            borderRadius: 18, padding: '18px 20px',
            position: 'relative', overflow: 'hidden',
          }}>
            {/* Cercles déco */}
            <div style={{ position: 'absolute', bottom: -30, right: -30, width: 120, height: 120, background: 'rgba(255,255,255,0.05)', borderRadius: '50%', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', top: -20, left: -20, width: 80, height: 80, background: 'rgba(255,255,255,0.03)', borderRadius: '50%', pointerEvents: 'none' }} />

            {/* Logo + badge */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, position: 'relative', zIndex: 1 }}>
              <img
                src="/logo-sogas1.png" alt="SOGAS"
                style={{ height: 30, objectFit: 'contain' }}
                onError={e => { e.target.src = '/logo-sogas.png'; }}
              />
              <span style={{ background: 'rgba(255,255,255,0.18)', borderRadius: 20, padding: '3px 10px', color: '#fff', fontSize: '0.58rem', fontWeight: 600 }}>● ACTIF</span>
            </div>

            {/* Identité */}
            <div style={{ position: 'relative', zIndex: 1 }}>
              <p style={{ color: '#fff', fontWeight: 700, fontSize: '1rem', margin: '0 0 2px 0', textTransform: 'uppercase' }}>
                {employe.prenom} {employe.nom}
              </p>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.72rem', margin: '0 0 2px 0' }}>{employe.poste || 'Employé'}</p>
              {infosSub && <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.65rem', margin: '0 0 14px 0' }}>{infosSub}</p>}
            </div>

            {/* Séparateur */}
            <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', marginBottom: 12, position: 'relative', zIndex: 1 }} />

            {/* Matricule + photo */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', position: 'relative', zIndex: 1 }}>
              <div>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.55rem', letterSpacing: '1.2px', margin: '0 0 3px 0', textTransform: 'uppercase' }}>Matricule</p>
                <p style={{ color: '#fff', fontSize: '1.45rem', fontWeight: 800, letterSpacing: 3, margin: 0 }}>{employe.matricule}</p>
              </div>
              <PhotoProfil employe={employe} taille={70} dark={true} editable={false} />
            </div>
          </div>

          {/* VERSO */}
          <div style={{ background: '#fff', borderRadius: 18, padding: '18px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <img
              src="/logo-sogas1.png" alt="SOGAS"
              style={{ height: 28, objectFit: 'contain', display: 'block', marginBottom: 12 }}
              onError={e => { e.target.src = '/logo-sogas.png'; }}
            />
            <div style={{ borderTop: '0.5px solid #f0f0f0', marginBottom: 14 }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              {/* Infos gauche */}
              <div>
                {[
                  { l: 'Poste',       v: employe.poste || 'Employé' },
                  { l: 'Département', v: employe.departement },
                  { l: 'Site',        v: siteEmploye(employe) },
                ].map(({ l, v }) => (
                  <div key={l} style={{ marginBottom: 9 }}>
                    <p style={{ fontSize: '0.55rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.8px', margin: '0 0 2px 0' }}>{l}</p>
                    <p style={{ fontSize: '0.82rem', fontWeight: 600, color: '#2c2c2c', margin: 0 }}>{v || '–'}</p>
                  </div>
                ))}
              </div>

              {/* QR droite */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {qrImageData
                  ? <img src={qrImageData} alt="QR Code SOGAS" style={{ width: 95, height: 95, borderRadius: 10, border: '1px solid #eee', background: '#f9f9f9', display: 'block' }} />
                  : <div style={{ width: 95, height: 95, borderRadius: 10, border: '1px solid #eee', background: '#f9f9f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><span style={{ fontSize: 9, color: '#bbb', textAlign: 'center', padding: 4 }}>Chargement…</span></div>
                }
                <p style={{ fontSize: '0.55rem', color: '#bbb', textAlign: 'center', marginTop: 5, maxWidth: 95 }}>Scanner pour pointer</p>
              </div>
            </div>
          </div>

        </div>

        {/* Copyright */}
        <div style={{ textAlign: 'center', padding: '8px 0 16px 0' }}>
          <p style={{ fontSize: '0.55rem', color: '#ccc', margin: '0 0 2px 0' }}>© 2025 SOGAS · Tous droits réservés</p>
          <p style={{ fontSize: '0.54rem', color: '#ccc', margin: 0 }}>
            Développé par <span style={{ color: '#1B5E20', fontWeight: 600 }}>Abdoul Aziz Talla</span>
          </p>
        </div>

      </div>
    );
  }
  /* ══════════════════════════════════════════════════════════ */

  const ROLES_SECURITE = ['admin', 'rh_principal', 'drh', 'assistant_rh'];
  const peutVoirSecurite = ROLES_SECURITE.includes(userConnecte?.role);

  // RH-PAIE-1 : 'admin' n'est jamais inclus ici (contrairement à peutVoirSecurite) —
  // l'accès au dossier salarial n'est jamais automatique pour un rôle technique.
  const ROLES_DOSSIER_SALARIAL = ['drh', 'rh_principal', 'assistant_rh'];
  const peutVoirDossierSalarial = ROLES_DOSSIER_SALARIAL.includes(userConnecte?.role)
    || userConnecte?.permissions_effectives?.peut_voir_dossier_salarial === true;

  const tousOnglets = [
    { n: 1, Ico: User, label: 'Identité' },
    { n: 2, Ico: Heart, label: 'Famille' },
    { n: 3, Ico: MapPin, label: 'Coordonnées' },
    { n: 4, Ico: Briefcase, label: 'Poste' },
    { n: 5, Ico: TrendingUp, label: 'Carrière' },
    { n: 6, Ico: AlertTriangle, label: 'Discipline' },
    { n: 7, Ico: GraduationCap, label: 'Formation' },
    { n: 8, Ico: CreditCard, label: 'Carte' },
    ...(peutVoirDossierSalarial ? [{ n: 11, Ico: DollarSign, label: 'Dossier salarial' }] : []),
    ...(peutVoirSecurite ? [{ n: 10, Ico: Shield, label: 'Sécurité' }] : []),
  ];

  // Un employé voit Mon Espace + Carte uniquement
  const onglets = estEmploye
    ? [{ n: 9, Ico: Home, label: 'Mon Espace' }, { n: 8, Ico: CreditCard, label: 'Ma Carte' }]
    : tousOnglets;

  const BtnsSave = ({ num }) => (
    <div style={{ display: 'flex', gap: 6 }}>
      <BtnSave onClick={() => sauvegarderEdit(formEdit)} loading={saving} />
      <BtnCancel onClick={annulerEdit} />
    </div>
  );

  // Actions de la fiche, présentées dans le bandeau de commande (menu « Actions »).
  const actionsFiche = [
    ...(synthese?.actions?.rh && synthese?.actions?.en_activite ? [
      { groupe: 'Faire évoluer la situation' },
      { libelle: 'Corriger une information', Icone: Pencil, onClick: () => changerOnglet('personnel') },
      peutCreerAffectation && { libelle: 'Changer d\'affectation', Icone: ArrowRightLeft, onClick: ouvrirNouvelleAffectation },
      !employe.poste_id && { libelle: 'Changer de responsable', Icone: UserRound, onClick: () => setActionOuverte('responsable') },
      { libelle: 'Changer de métier', Icone: Briefcase, onClick: () => setActionOuverte('metier') },
      synthese?.contrat && { libelle: 'Modifier le contrat', Icone: FileSignature, onClick: () => setActionOuverte('contrat') },
      peutCreerPromotion && { libelle: 'Promotion ou reclassement', Icone: TrendingUp, onClick: ouvrirNouvellePromotion },
      peutCreerAugmentation && { libelle: 'Évolution salariale', Icone: DollarSign, onClick: ouvrirNouvelleAugmentation },
      { groupe: 'Statut' },
      employe.statut === 'Actif' && { libelle: 'Suspendre', Icone: PauseCircle, onClick: () => setActionOuverte('suspension') },
      employe.statut === 'Suspendu' && { libelle: 'Réintégrer', Icone: PlayCircle, onClick: () => setActionOuverte('reintegration') },
      !(synthese?.programmes || []).some((x) => x.type === 'SORTIE') && { libelle: 'Sortie du salarié', Icone: LogOut, onClick: () => setActionOuverte('sortie'), danger: true },
      { groupe: 'Dossier' },
      ['drh', 'rh_principal'].includes(userConnecte?.role) && { libelle: 'Ajouter une sanction', Icone: AlertTriangle, onClick: () => { setFormSanction(videFormSanction); setModalSanction(true); } },
      { libelle: 'Ajouter une formation', Icone: BadgePlus, onClick: () => { setFormFormation(videFormFormation); setModalFormation(true); } },
    ] : []),
    { groupe: 'Autres' },
    ['drh', 'rh_principal', 'assistant_rh'].includes(userConnecte?.role) && { libelle: 'Exporter le dossier (PDF)', Icone: FileDown, onClick: ouvrirDossier },
    ['admin', 'drh', 'rh_principal', 'directeur_general', 'directeur_adjoint', 'assistant_rh', 'prepose_regional', 'responsable_site', 'chef_departement', 'chef_service', 'chef_equipe'].includes(userConnecte?.role)
      && { libelle: 'Droits et accès', Icone: KeyRound, onClick: () => navigate(`/employes/${id}/permissions`) },
    ongletsAutorises.includes('acces') && { libelle: 'Carte salarié', Icone: CreditCard, onClick: () => changerOnglet('acces') },
              ];

  return (
    <Layout>
      <div className={estEmploye ? undefined : 'rh-page'} style={estEmploye
        ? { maxWidth: 480, margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif', paddingBottom: 40, background: '#f7f7f7', minHeight: '100vh' }
        : undefined
      }>

        {msg && (
          <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 2000, background: msg.type === 'err' ? '#FFEBEE' : '#E8F5E9', color: msg.type === 'err' ? ROUGE : VERT, border: `1px solid ${msg.type === 'err' ? '#FFCDD2' : '#C8E6C9'}`, borderRadius: 10, padding: '12px 20px', fontSize: 13, fontWeight: 600, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' }}>
            {msg.texte}
          </div>
        )}

        {!estEmploye && (
          <EnTetePage fil={[{ libelle: titreListe, vers: lienListe }, { libelle: `${employe.prenom || ''} ${(employe.nom || '').toUpperCase()}`.trim() }]}
            actions={<MenuActions items={actionsFiche} />} />
        )}
        <div className={estEmploye ? undefined : 'rh-feuille'}>
        {!estEmploye && (
          <>
            <BandeauSalarie
              synthese={synthese}
              photoModifiable={['rh_principal', 'assistant_rh', 'drh', 'admin'].includes(userConnecte?.role)}
              onPhotoMaj={(url) => { setEmploye(e => ({ ...e, photo_url: url })); setSynthese(sy => ({ ...sy, salarie: { ...sy.salarie, photo_url: url } })); }}
              onVoirDossier={() => changerOnglet('apercu')}
            />
            <Onglets libelle="Sections du dossier" actif={ongletActif} onChange={changerOnglet}
              onglets={[
                { cle: 'apercu', libelle: 'Aperçu', Icone: LayoutDashboard },
                { cle: 'emploi', libelle: 'Emploi', Icone: Briefcase },
                { cle: 'historique', libelle: 'Historique', Icone: History },
                { cle: 'personnel', libelle: 'Personnel', Icone: User },
                { cle: 'documents', libelle: 'Documents', Icone: FolderOpen },
                { cle: 'paie', libelle: 'Paie', Icone: Wallet },
                { cle: 'acces', libelle: 'Accès', Icone: KeyRound },
              ].filter((o) => ongletsAutorises.includes(o.cle))}
            />
          </>
        )}

        {estEmploye && (<>
        <div className="sg-bandeau-clair" style={{ background: `linear-gradient(135deg, ${VERT} 0%, #2E7D32 60%, #1B5E20 100%)`, borderRadius: '0 0 20px 20px', padding: '24px 28px 20px', marginBottom: 0, color: '#fff', position: 'relative', overflow: 'hidden' }}>
          <div style={{ position: 'absolute', top: -30, right: -30, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
          {/* Cloche notifs — position absolue top-right */}
          <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
            <NotificationBell couleurIcone="#fff" taille={18} />
          </div>
          {estEmploye && (
            <button onClick={() => navigate('/accueil-employe')} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 8, padding: '4px 10px', fontSize: '0.72rem', cursor: 'pointer', marginBottom: 12, fontWeight: 600 }}>← Accueil</button>
          )}
          {!estEmploye && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button onClick={() => navigate('/employes')} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>← Retour</button>
              {['admin', 'drh', 'rh_principal', 'directeur_general', 'directeur_adjoint', 'assistant_rh', 'prepose_regional', 'responsable_site', 'chef_departement', 'chef_service', 'chef_equipe'].includes(userConnecte?.role) && (
                <button onClick={() => navigate(`/employes/${id}/permissions`)} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                  <Lock size={13} style={{ marginRight:5 }} />Droits &amp; Accès
                </button>
              )}
              {['admin', 'drh', 'rh_principal', 'assistant_rh', 'directeur_general', 'directeur_adjoint'].includes(userConnecte?.role) && (
                <button onClick={() => navigate(`/employes/${id}/rapport`)} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                  <Clipboard size={13} style={{ marginRight:5 }} />Rapport
                </button>
              )}
              {['admin', 'drh', 'rh_principal', 'assistant_rh'].includes(userConnecte?.role) && (
                <button onClick={ouvrirDossier} style={{ background: '#fff', border: 'none', color: VERT, borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={VERT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                  Dossier complet
                </button>
              )}
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <PhotoProfil
              employe={employe}
              taille={72}
              clair
              editable={['rh_principal','assistant_rh','drh','admin'].includes(userConnecte?.role)}
              onPhotoMaj={(url) => setEmploye(e => ({ ...e, photo_url: url }))}
            />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 22, fontWeight: 700 }}>{employe.prenom} {employe.nom}</div>
              <div style={{ opacity: 0.85, fontSize: 13, marginTop: 3 }}>{employe.poste || '–'} · {employe.departement || siteEmploye(employe)}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <span style={{ background: estActif ? '#4CAF50' : '#F44336', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{employe.statut?.toUpperCase() || 'ACTIF'}</span>
                {employe.matricule && <span style={{ background: 'rgba(255,255,255,0.2)', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>#{employe.matricule}</span>}
                {employe.type_contrat && <span style={{ background: 'rgba(255,255,255,0.15)', padding: '3px 10px', borderRadius: 20, fontSize: 11 }}>{employe.type_contrat}</span>}
              </div>
            </div>
          </div>
        </div>

        <div style={{ position: 'sticky', top: 0, zIndex: 100, background: '#fff', borderBottom: estEmploye ? `2px solid #f0f0f0` : `2px solid ${GRIS_BORD}`, boxShadow: estEmploye ? 'none' : '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'stretch' }}>
          {ongletsDebordeGauche && (
            <button onClick={() => defilerOnglets(-1)} aria-label="Faire défiler les onglets vers la gauche" style={{ flexShrink: 0, border: 'none', borderRight: `1px solid ${GRIS_BORD}`, background: '#fff', color: VERT, cursor: 'pointer', padding: '0 8px', fontSize: 14, fontWeight: 700 }}>‹</button>
          )}
          <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
            <div
              ref={ongletsRef}
              onScroll={majDebordementOnglets}
              style={{ display: 'flex', overflowX: 'auto', scrollbarWidth: 'none' }}
            >
              {onglets.map(o => (
                <button key={o.n} onClick={() => setOnglet(o.n)} style={{
                  padding: estEmploye ? '12px 20px' : '12px 16px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: estEmploye ? '0.82rem' : 12,
                  fontWeight: 600,
                  color: onglet === o.n ? VERT : '#888',
                  borderBottom: onglet === o.n ? `2px solid ${VERT}` : '2px solid transparent',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                  flexShrink: 0,
                }}>
                  {(!estEmploye && o.Ico) && <o.Ico size={13} />} {o.label}
                </button>
              ))}
            </div>
            {ongletsDebordeDroite && (
              <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: 28, background: 'linear-gradient(to right, transparent, #fff)', pointerEvents: 'none' }} />
            )}
          </div>
          {ongletsDebordeDroite && (
            <button onClick={() => defilerOnglets(1)} aria-label="Faire défiler les onglets vers la droite" style={{ flexShrink: 0, border: 'none', borderLeft: `1px solid ${GRIS_BORD}`, background: '#fff', color: VERT, cursor: 'pointer', padding: '0 8px', fontSize: 14, fontWeight: 700 }}>›</button>
          )}
        </div>

        </>)}

        <div style={estEmploye ? { padding: '14px', background: '#f7f7f7' } : undefined} className={estEmploye ? undefined : 'rh-feuille-corps'}
          role={estEmploye ? undefined : 'tabpanel'} id={estEmploye ? undefined : `panneau-${ongletActif}`} aria-labelledby={estEmploye ? undefined : `onglet-${ongletActif}`}>

        {/* Sécurité : un employé ne peut voir que les onglets 8 et 9 */}
        {estEmploye && onglet !== 8 && onglet !== 9 && null}

        {!estEmploye && ongletActif === 'apercu' && <ApercuSalarie synthese={synthese} onAllerOnglet={changerOnglet} />}
        {!estEmploye && ongletActif === 'documents' && <DocumentsSalarie employeId={id} peutGerer={!!synthese?.actions?.documents} onMessage={(m) => { showMsg(m); chargerEmploye(); }} />}

        {!estEmploye && voir(1) && (
          <>
            <Card>
              <CardTitre Ico={User} titre="Informations personnelles"
                action={peutModifier ? (editOnglet === 1 ? <BtnsSave num={1} /> : <BtnEdit onClick={() => demarrerEdit(1, { prenom: employe.prenom, nom: employe.nom, date_naissance: employe.date_naissance?.split('T')?.[0] || '', lieu_naissance: employe.lieu_naissance, genre: employe.genre, nationalite: employe.nationalite, situation_familiale: employe.situation_familiale })} />) : null}
              />
              <Grille>
                <Champ label="Prénom" valeur={employe.prenom} edit={editOnglet === 1} input={<InputEdit value={formEdit.prenom} onChange={e => setFormEdit({...formEdit, prenom: e.target.value})} />} />
                <Champ label="Nom" valeur={employe.nom} edit={editOnglet === 1} input={<InputEdit value={formEdit.nom} onChange={e => setFormEdit({...formEdit, nom: e.target.value})} />} />
                <Champ label="Date de naissance" valeur={fmtDate(employe.date_naissance)} edit={editOnglet === 1} input={<InputEdit type="date" value={formEdit.date_naissance} onChange={e => setFormEdit({...formEdit, date_naissance: e.target.value})} />} />
                <Champ label="Lieu de naissance" valeur={employe.lieu_naissance} edit={editOnglet === 1} input={<InputEdit value={formEdit.lieu_naissance} onChange={e => setFormEdit({...formEdit, lieu_naissance: e.target.value})} />} />
                <Champ label="Genre" valeur={employe.genre === 'M' ? 'Masculin' : employe.genre === 'F' ? 'Féminin' : '–'} edit={editOnglet === 1} input={<InputEdit value={formEdit.genre} onChange={e => setFormEdit({...formEdit, genre: e.target.value})} options={[{v:'M',l:'Masculin'},{v:'F',l:'Féminin'}]} />} />
                <Champ label="Nationalité" valeur={employe.nationalite} edit={editOnglet === 1} input={<InputEdit value={formEdit.nationalite} onChange={e => setFormEdit({...formEdit, nationalite: e.target.value})} />} />
                <Champ label="Situation familiale" valeur={({ Celibataire: 'Célibataire', Marie: 'Marié(e)', Divorce: 'Divorcé(e)', Veuf: 'Veuf / Veuve' })[employe.situation_familiale] || employe.situation_familiale} edit={editOnglet === 1} input={<InputEdit value={formEdit.situation_familiale} onChange={e => setFormEdit({...formEdit, situation_familiale: e.target.value})} options={[{v:'Celibataire',l:'Célibataire'},{v:'Marie',l:'Marié(e)'},{v:'Divorce',l:'Divorcé(e)'},{v:'Veuf',l:'Veuf/Veuve'}]} />} />
              </Grille>
            </Card>
            <Card>
              <CardTitre Ico={CreditCard} titre="Pièce d'identité et identifiants sociaux"
                action={peutModifier ? (editOnglet === 11 ? <BtnsSave num={11} /> : <BtnEdit onClick={() => demarrerEdit(11, { cni_numero: employe.cni_numero, cni_expiration: employe.cni_expiration?.split('T')?.[0] || '', numero_ipres: employe.numero_ipres, numero_css: employe.numero_css })} />) : null}
              />
              <Grille>
                <Champ label="N° CNI" valeur={employe.cni_numero} edit={editOnglet === 11} input={<InputEdit value={formEdit.cni_numero} onChange={e => setFormEdit({...formEdit, cni_numero: e.target.value})} />} />
                <Champ label="Expiration CNI" valeur={fmtDate(employe.cni_expiration)} edit={editOnglet === 11} input={<InputEdit type="date" value={formEdit.cni_expiration} onChange={e => setFormEdit({...formEdit, cni_expiration: e.target.value})} />} />
                <Champ label="N° IPRES" valeur={employe.numero_ipres} edit={editOnglet === 11} input={<InputEdit value={formEdit.numero_ipres} onChange={e => setFormEdit({...formEdit, numero_ipres: e.target.value})} />} />
                <Champ label="N° CSS" valeur={employe.numero_css} edit={editOnglet === 11} input={<InputEdit value={formEdit.numero_css} onChange={e => setFormEdit({...formEdit, numero_css: e.target.value})} />} />
              </Grille>
            </Card>
          </>
        )}

        {!estEmploye && voir(3) && (
          <Card>
            <CardTitre Ico={MapPin} titre="Coordonnées"
              action={peutModifier ? (editOnglet === 3 ? <BtnsSave num={3} /> : <BtnEdit onClick={() => demarrerEdit(3, { telephone: employe.telephone, email: employe.email, adresse: employe.adresse, telephone_urgence: employe.telephone_urgence, contact_urgence_nom: employe.contact_urgence_nom, contact_urgence_lien: employe.contact_urgence_lien })} />) : null}
            />
            <Grille>
              <Champ label="Téléphone" valeur={employe.telephone} edit={editOnglet === 3} input={<InputEdit value={formEdit.telephone} onChange={e => setFormEdit({...formEdit, telephone: e.target.value})} />} />
              <Champ label="Email" valeur={employe.email} edit={editOnglet === 3} input={<InputEdit type="email" value={formEdit.email} onChange={e => setFormEdit({...formEdit, email: e.target.value})} />} />
              <Champ label="Adresse" valeur={employe.adresse} edit={editOnglet === 3} input={<InputEdit value={formEdit.adresse} onChange={e => setFormEdit({...formEdit, adresse: e.target.value})} />} />
              <Champ label="Contact d'urgence" valeur={employe.contact_urgence_nom} edit={editOnglet === 3} input={<InputEdit value={formEdit.contact_urgence_nom} onChange={e => setFormEdit({...formEdit, contact_urgence_nom: e.target.value})} />} />
              <Champ label="Lien avec le salarié" valeur={employe.contact_urgence_lien} edit={editOnglet === 3} input={<InputEdit value={formEdit.contact_urgence_lien} onChange={e => setFormEdit({...formEdit, contact_urgence_lien: e.target.value})} />} />
              <Champ label="Téléphone d'urgence" valeur={employe.telephone_urgence} edit={editOnglet === 3} input={<InputEdit value={formEdit.telephone_urgence} onChange={e => setFormEdit({...formEdit, telephone_urgence: e.target.value})} />} />
            </Grille>
          </Card>
        )}

        {!estEmploye && voir(2) && voitSensible && (
          <>
            <Card>
              <CardTitre Ico={Heart} titre={`Épouses (${epouses.length}/4)`}
                action={peutModifier ? <button onClick={() => { setEpouseEdit(null); setFormEpouse({ nom: '', prenom: '', date_naissance: '', date_mariage: '', numero_certificat: '', statut: 'Mariée' }); setModalEpouse(true); }} disabled={epouses.filter(e => e.statut === 'Mariée').length >= 4} className="rh-btn rh-btn-petit rh-btn-lien"><Plus size={13} aria-hidden="true" />Ajouter</button> : null}
              />
              {epouses.length === 0 ? <p style={{ color: '#bbb', textAlign: 'center', padding: 20, fontSize: 13 }}>Aucune épouse enregistrée</p> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {epouses.map(e => (
                    <div key={e.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderRadius: 8, background: GRIS, border: `1px solid ${GRIS_BORD}` }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{e.prenom} {e.nom}</div>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Mariage : {fmtDate(e.date_mariage)}</div>
                        <Justificatif personne={e} type="conjoint" peutVerifier={peutVerifierJustificatif} onVerifier={() => verifierJustificatif('epouses', e.id)} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Badge texte={e.statut} couleur={e.statut === 'Mariée' ? 'vert' : e.statut === 'Décédée' ? 'rouge' : 'gris'} />
                        {peutModifier && <button onClick={() => editerEpouse(e)} style={{ background: 'none', border: `1px solid ${GRIS_BORD}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, display:'inline-flex', alignItems:'center' }}><Pencil size={12} /></button>}
                        {peutModifier && <button onClick={() => supprimerEpouse(e.id, e)} style={{ background: 'none', border: '1px solid #FFCDD2', color: ROUGE, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, display:'inline-flex', alignItems:'center' }}><Trash2 size={12} /></button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
            <Card>
              <CardTitre Ico={Baby} titre={`Enfants (${enfants.length})`}
                action={peutModifier ? <button onClick={() => { setEnfantEdit(null); setFormEnfant({ prenom: '', nom: '', date_naissance: '', sexe: '', lien: 'Enfant' }); setModalEnfant(true); }} className="rh-btn rh-btn-petit rh-btn-lien"><Plus size={13} aria-hidden="true" />Ajouter</button> : null}
              />
              {enfants.length === 0 ? <p style={{ color: '#bbb', textAlign: 'center', padding: 20, fontSize: 13 }}>Aucun enfant enregistré</p> : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {enfants.map(e => (
                    <div key={e.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderRadius: 8, background: GRIS, border: `1px solid ${GRIS_BORD}` }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{e.prenom} {e.nom}</div>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{e.sexe === 'M' ? 'Garçon' : e.sexe === 'F' ? 'Fille' : <span style={{ color: 'var(--sg-alerte)' }}>Sexe non renseigné</span>} · Né(e) le {fmtDate(e.date_naissance)}</div>
                        <Justificatif personne={e} type="enfant" peutVerifier={peutVerifierJustificatif} onVerifier={() => verifierJustificatif('enfants', e.id)} />
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Badge texte={e.a_charge ? 'À charge' : 'Non'} couleur={e.a_charge ? 'vert' : 'gris'} />
                        {peutModifier && <button onClick={() => editerEnfant(e)} style={{ background: 'none', border: `1px solid ${GRIS_BORD}`, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, display:'inline-flex', alignItems:'center' }}><Pencil size={12} /></button>}
                        {peutModifier && <button onClick={() => supprimerEnfant(e.id, e)} style={{ background: 'none', border: '1px solid #FFCDD2', color: ROUGE, borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, display:'inline-flex', alignItems:'center' }}><Trash2 size={12} /></button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}

        {/* Module 1 (livraison 2) : Emploi lisible + actions nommées ; Historique en frise unique. */}
        {!estEmploye && ongletActif === 'emploi' && (
          <EmploiSalarie salarieId={id} synthese={synthese} subordonnes={subordonnes} rafraichir={rafraichir}
            onAction={(cle) => (cle === 'mutation' ? ouvrirNouvelleAffectation() : setActionOuverte(cle))}
            onMessage={(m) => { showMsg(m); actionTerminee(); }} />
        )}
        {!estEmploye && ongletActif === 'historique' && (
          <HistoriqueSalarie salarieId={id} rafraichir={rafraichir} peutAnnuler={!!synthese?.actions?.annuler}
            onAnnuler={(x) => setAnnulationDemandee(x)}
            onCorrigerAffectation={peutCreerAffectation ? (aid) => { const a = affectations.find((x) => x.id === aid); if (a) editerAffectation(a); } : null}
            onAnnulerAffectation={peutCreerAffectation ? (aid) => { const a = affectations.find((x) => x.id === aid); if (a) ouvrirAnnulation(a); } : null}
            onLeverSanction={['drh', 'rh_principal'].includes(userConnecte?.role) ? (sid) => { const x = sanctions.find((y) => y.id === sid); if (x) leverSanctionUI(sid, x); } : null} />
        )}

        {/* ─── DOSSIER SALARIAL (onglet 11, RH-PAIE-1) ─── */}
        {!estEmploye && voir(11) && peutVoirDossierSalarial && (
          <>
            {/* Module 1 (D-E8) : identifiants sociaux, utiles à la paie (visibles RH, paie, salarié). */}
            <Card>
              <CardTitre Ico={CreditCard} titre="Identifiants sociaux" />
              <Grille>
                <Champ label="N° IPRES" valeur={employe.numero_ipres} />
                <Champ label="N° CSS" valeur={employe.numero_css} />
              </Grille>
            </Card>
            <DossierSalarialTab employeId={id} userConnecte={userConnecte} employe={employe} />
          </>
        )}

        {/* ─── SÉCURITÉ COMPTE (onglet 10, RH uniquement) ─── */}
        {!estEmploye && voir(10) && (
          <Card>
            <CardTitre Ico={KeyRound} titre="Rôle et habilitations"
              action={<button type="button" className="rh-btn rh-btn-petit" onClick={() => navigate(`/employes/${id}/permissions`)}>Gérer les droits et accès<ChevronRight size={14} aria-hidden="true" /></button>} />
            <p style={{ margin: 0, fontSize: 13, color: 'var(--sg-texte-3)' }}>
              Le rôle et les habilitations se modifient uniquement dans « Droits et accès » : acte distinct, motivé et journalisé, jamais sur son propre compte.
            </p>
          </Card>
        )}

        {!estEmploye && voir(10) && peutVoirSecurite && (
          <SectionSecurite
            employe={employe}
            userConnecte={userConnecte}
            onRefresh={chargerEmploye}
          />
        )}

        {/* ─── MON ESPACE (onglet 9, employé uniquement) ─── */}
        {onglet === 9 && estEmploye && (() => {
          const fmtD = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '–';
          const cardStyle = { background: '#fff', borderRadius: 12, border: '1px solid #E0E0E0', borderLeft: `4px solid ${VERT}`, padding: '20px 24px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' };
          const titreSectionStyle = { fontWeight: 700, fontSize: 15, color: VERT, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 };
          const labelStyle = { fontSize: 10, color: '#999', textTransform: 'uppercase', letterSpacing: 0.8, fontWeight: 600, marginBottom: 2 };
          const valeurStyle = { fontSize: 14, color: '#1a1a1a', fontWeight: 500, marginBottom: 12 };

          // Fusion affectations + promotions pour la timeline (sans montants)
          const mouvements = [
            ...affectations.map(a => ({ date: a.date_affectation, type: 'Affectation', Ico: MapPin,  couleur: '#1565C0', bg: '#E3F2FD', desc: [a.nouveau_poste, a.nouveau_departement, a.nouveau_site].filter(Boolean).join(' · ') || '–' })),
            ...promotions.map(p => ({ date: p.date_promotion, type: 'Promotion',    Ico: Award,   couleur: '#E65100', bg: '#FFF3E0', desc: p.nouveau_titre || '–' })),
          ].sort((a, b) => new Date(b.date) - new Date(a.date));

          const derniers5 = [
            ...affectations.map(a => ({ date: a.date_affectation, type: 'Affectation', desc: [a.nouveau_poste, a.nouveau_site].filter(Boolean).join(' · ') || '–', Ico: MapPin })),
            ...augmentations.map(a => ({ date: a.date_augmentation, type: 'Augmentation', desc: 'Revalorisation salariale', Ico: DollarSign })),
            ...promotions.map(p => ({ date: p.date_promotion, type: 'Promotion', desc: p.nouveau_titre || '–', Ico: Award })),
          ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);

          return (
            <div>
              {/* Section 1 — Mes Informations */}
              <div style={cardStyle}>
                <div style={titreSectionStyle}><User size={15} />Mes Informations</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '4px 32px' }}>
                  {[
                    { label: 'Prénom', val: employe.prenom },
                    { label: 'Nom', val: employe.nom },
                    { label: 'Poste', val: employe.poste || '–' },
                    { label: 'Département', val: employe.departement || '–' },
                    { label: 'Site', val: siteEmploye(employe) },
                    { label: 'Type de contrat', val: employe.type_contrat || '–' },
                    { label: 'Date d\'embauche', val: fmtD(employe.date_embauche) },
                    { label: 'Statut', val: employe.statut || '–' },
                  ].map(({ label, val }) => (
                    <div key={label}>
                      <div style={labelStyle}>{label}</div>
                      <div style={valeurStyle}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 2 — Mon Évolution */}
              <div style={cardStyle}>
                <div style={titreSectionStyle}><TrendingUp size={15} />Mon Évolution</div>
                {mouvements.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: '#bbb', fontSize: 13 }}>Aucun mouvement enregistré</div>
                ) : (
                  <div style={{ position: 'relative', paddingLeft: 28 }}>
                    <div style={{ position: 'absolute', left: 10, top: 8, bottom: 8, width: 2, background: `linear-gradient(to bottom, ${VERT}, #A5D6A7)` }} />
                    {mouvements.map((m, i) => (
                      <div key={i} style={{ position: 'relative', marginBottom: i < mouvements.length - 1 ? 16 : 0 }}>
                        <div style={{ position: 'absolute', left: -22, top: 10, width: 24, height: 24, borderRadius: '50%', background: m.bg, border: `2px solid ${m.couleur}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {m.Ico && <m.Ico size={12} color={m.couleur} />}
                        </div>
                        <div style={{ background: '#FAFAFA', borderRadius: 8, padding: '10px 14px', border: '1px solid #F0F0F0' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ background: m.bg, color: m.couleur, borderRadius: 20, padding: '2px 8px', fontSize: 10, fontWeight: 700 }}>{m.type}</span>
                            <span style={{ fontSize: 11, color: '#888' }}>{fmtD(m.date)}</span>
                          </div>
                          <div style={{ fontSize: 13, color: '#444', fontWeight: 500 }}>{m.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Section 3 — Mon Dossier */}
              <div style={{ ...cardStyle, borderLeft: `4px solid ${VERT}` }}>
                <div style={titreSectionStyle}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={VERT} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                  Mon Dossier
                </div>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 14 }}>
                  Votre dossier administratif complet — identité, carrière, formations.
                </div>
                <button onClick={telechargerDossierPDF} disabled={loadingPDF}
                  style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, opacity: loadingPDF ? 0.6 : 1 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  {loadingPDF ? 'Génération…' : 'Télécharger mon dossier PDF'}
                </button>
              </div>

              {/* Section 4 — Mes Changements récents */}
              <div style={cardStyle}>
                <div style={titreSectionStyle}><RefreshCw size={15} />Mes Changements récents</div>
                {derniers5.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px 0', color: '#bbb', fontSize: 13 }}>Aucun changement enregistré</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {derniers5.map((m, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, background: '#FAFAFA', border: '1px solid #F0F0F0' }}>
                        <div style={{ flexShrink: 0, color: '#888' }}>{m.Ico && <m.Ico size={18} />}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>{m.desc}</div>
                          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{m.type} · {fmtD(m.date)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {!estEmploye && voir(8) && (() => {
          const annee = new Date().getFullYear();
          const today = new Date().toISOString().slice(0, 10);
          const qrStatique = `SOGAS|EMP|${employe.matricule}|${employe.nom}|${today}`;

          const imprimerCarte = () => {
            const rectoEl = document.getElementById('carte-recto');
            const versoEl = document.getElementById('carte-verso');
            if (!rectoEl || !versoEl) return;
            const w = window.open('', '_blank');
            w.document.write(`<html><head><title>Carte SOGAS — ${employe.prenom} ${employe.nom}</title>
<style>
  @page { size: A4 landscape; margin: 15mm; }
  body { margin: 0; display: flex; align-items: center; justify-content: center; gap: 24px; font-family: 'Segoe UI', sans-serif; background: #f5f5f5; }
  .carte { width: 340px; height: 210px; flex-shrink: 0; border-radius: 16px; overflow: hidden; }
  img { max-width: 100%; }
</style></head><body>
<div class="carte">${rectoEl.outerHTML}</div>
<div class="carte">${versoEl.outerHTML}</div>
<script>window.onload=()=>{ setTimeout(()=>window.print(), 400); }<\/script>
</body></html>`);
            w.document.close();
          };

          return (
            <Card>
            <CardTitre Ico={CreditCard} titre="Carte salarié" />
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, width: '100%' }}>

              {/* ─── RECTO ─── */}
              <div id="carte-recto" style={{
                width: '100%', maxWidth: 420, minHeight: 210,
                background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 55%, #1B5E20 100%)',
                borderRadius: 16, padding: 16,
                boxShadow: '0 8px 32px rgba(0,0,0,0.32)',
                position: 'relative', overflow: 'hidden',
                color: '#fff', fontFamily: 'Segoe UI, sans-serif',
                boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
              }}>
                {/* Filigrane tête de bœuf */}
                <svg style={{ position: 'absolute', bottom: -10, right: -10, opacity: 0.08, pointerEvents: 'none' }} width="120" height="120" viewBox="0 0 100 100">
                  <circle cx="50" cy="58" r="32" fill="white"/>
                  <ellipse cx="50" cy="32" rx="18" ry="22" fill="white"/>
                  <path d="M32 30 Q18 10 22 4 Q30 18 38 24" fill="white"/>
                  <path d="M68 30 Q82 10 78 4 Q70 18 62 24" fill="white"/>
                  <ellipse cx="28" cy="38" rx="8" ry="6" fill="white" transform="rotate(-20 28 38)"/>
                  <ellipse cx="72" cy="38" rx="8" ry="6" fill="white" transform="rotate(20 72 38)"/>
                  <circle cx="41" cy="28" r="3" fill="#1B5E20"/>
                  <circle cx="59" cy="28" r="3" fill="#1B5E20"/>
                  <ellipse cx="50" cy="44" rx="7" ry="5" fill="#1B5E20" opacity="0.6"/>
                  <circle cx="47" cy="44" r="2" fill="white" opacity="0.8"/>
                  <circle cx="53" cy="44" r="2" fill="white" opacity="0.8"/>
                </svg>

                {/* En-tête recto */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div>
                    <div style={{ background: 'white', borderRadius: 6, padding: '3px 8px', display: 'inline-block' }}>
                      <img src="/logo-sogas.png" alt="SOGAS" style={{ height: 20, display: 'block' }} />
                    </div>
                    <div style={{ fontSize: 7, opacity: 0.65, marginTop: 3, letterSpacing: 1, textTransform: 'uppercase' }}>Carte d'Employé · {annee}</div>
                  </div>
                  <div style={{ background: estActif ? 'rgba(76,175,80,0.9)' : 'rgba(244,67,54,0.9)', padding: '3px 9px', borderRadius: 20, fontSize: 8, fontWeight: 800, letterSpacing: 1, border: '1px solid rgba(255,255,255,0.4)', flexShrink: 0 }}>
                    {estActif ? 'ACTIF' : 'INACTIF'}
                  </div>
                </div>

                {/* Identité recto */}
                <div style={{ marginBottom: 10, flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '1.05rem', fontWeight: 800, lineHeight: 1.25, textShadow: '0 1px 4px rgba(0,0,0,0.3)', wordBreak: 'break-word', overflow: 'hidden' }}>
                    {employe.prenom} {employe.nom}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.92, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {employe.poste || 'Employé'}
                  </div>
                  <div style={{ fontSize: 9, opacity: 0.75, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {[employe.categorie && `${familleProfessionnelle ? humaniserFamilleProfessionnelle(familleProfessionnelle) : 'À classer'} — Cat. ${employe.categorie}`, employe.departement, employe.site && libelleSite(employe.site)].filter(Boolean).join(' · ')}
                  </div>
                </div>

                {/* Pied recto */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                  <div>
                    <div style={{ fontSize: 7, opacity: 0.55, letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 3 }}>Matricule</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 800, letterSpacing: 2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {employe.matricule}
                    </div>
                  </div>
                  <PhotoProfil employe={employe} taille={70} dark={true} editable={false} />
                </div>
              </div>

              {/* ─── VERSO ─── */}
              <div id="carte-verso" style={{
                width: '100%', maxWidth: 420, minHeight: 210,
                background: '#fff', borderRadius: 16, padding: 16,
                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                border: `2px solid ${GRIS_BORD}`,
                display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                boxSizing: 'border-box', fontFamily: 'Segoe UI, sans-serif',
              }}>
                {/* Logo seul en haut */}
                <div style={{ marginBottom: 12 }}>
                  <img src="/logo-sogas.png" alt="SOGAS" style={{ height: 26, display: 'block' }} />
                </div>

                {/* Corps verso : deux colonnes */}
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flex: 1 }}>
                  {/* Colonne gauche — infos */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {[
                      { l: 'Poste',       v: employe.poste },
                      { l: 'Département', v: employe.departement },
                      { l: 'Site',        v: siteEmploye(employe) },
                    ].map(({ l, v }) => (
                      <div key={l} style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 7, color: '#666', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600, marginBottom: 2 }}>{l}</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#1a1a1a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v || '–'}</div>
                      </div>
                    ))}
                  </div>

                  {/* Colonne droite — QR Code */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flexShrink: 0 }}>
                    <QRCodeCanvas value={qrStatique} size={110} fgColor="#0F5C2E" bgColor="#ffffff" level="H" style={{ borderRadius: 8, display: 'block' }} />
                    <div style={{ fontSize: 7, color: '#999', textAlign: 'center', maxWidth: 110 }}>Identifiant employé</div>
                  </div>
                </div>
              </div>

              {/* Bouton imprimer — RH uniquement */}
              {['rh_principal', 'assistant_rh'].includes(userConnecte?.role) && (
                <button
                  onClick={imprimerCarte}
                  style={{ background: VERT, color: '#fff', border: 'none', borderRadius: 10, padding: '13px 36px', cursor: 'pointer', fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 4px 14px rgba(27,94,32,0.35)' }}>
                  <Printer size={16} style={{ marginRight:8 }} />Imprimer recto &amp; verso
                </button>
              )}
            </div>
            </Card>
          );
        })()}

        </div>
        </div>
      </div>

      {modalEpouse && (
        <Modal titre={epouseEdit ? 'Modifier épouse' : 'Ajouter une épouse'} onClose={() => setModalEpouse(false)}>
          <InputModal label="Nom" value={formEpouse.nom} onChange={e => setFormEpouse({...formEpouse, nom: e.target.value})} required />
          <InputModal label="Prénom" value={formEpouse.prenom} onChange={e => setFormEpouse({...formEpouse, prenom: e.target.value})} />
          <InputModal label="Date de naissance" value={formEpouse.date_naissance} onChange={e => setFormEpouse({...formEpouse, date_naissance: e.target.value})} type="date" />
          <InputModal label="Date de mariage" value={formEpouse.date_mariage} onChange={e => setFormEpouse({...formEpouse, date_mariage: e.target.value})} type="date" />
          <InputModal label="N° Certificat de mariage" value={formEpouse.numero_certificat} onChange={e => setFormEpouse({...formEpouse, numero_certificat: e.target.value})} />
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}><Paperclip size={13} style={{ marginRight:4 }} />{epouseEdit ? 'Remplacer le certificat de mariage (PDF ou image)' : 'Certificat de mariage (PDF ou image)'}{!epouseEdit && <span style={{ color: 'var(--sg-danger)' }}> *</span>}</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFormEpouse({...formEpouse, fichier_certificat: e.target.files[0]})} className="rh-champ-saisie" style={{ padding: '6px 10px' }} />
            <div style={{ fontSize: 12, color: 'var(--sg-texte-3)', marginTop: 4 }}>
              {epouseEdit ? (epouseEdit.certificat_url ? 'Une pièce est déposée. Une nouvelle pièce devra être vérifiée à nouveau par un RH.' : 'Aucune pièce déposée : sans certificat vérifié, aucun droit n\'est ouvert (IPM).') : 'Obligatoire. La pièce devra ensuite être vérifiée par un RH pour ouvrir les droits (IPM).'}
            </div>
          </div>
          <SelectModal label="Statut" value={formEpouse.statut} onChange={e => setFormEpouse({...formEpouse, statut: e.target.value})} options={[{v:'Mariée',l:'Mariée'},{v:'Divorcée',l:'Divorcée'},{v:'Décédée',l:'Décédée'},{v:'Séparée',l:'Séparée'}]} />
          <BtnsModal onCancel={() => setModalEpouse(false)} onSave={soumettreEpouse} />
        </Modal>
      )}

      {modalAffectation && (
        <Modal titre={affectationEdit ? 'Corriger un mouvement (date, motif, document)' : 'Changer d\'affectation'} onClose={() => setModalAffectation(false)}>
          <InputModal label="Date d'affectation" value={formAffectation.date_affectation} onChange={e => setFormAffectation({...formAffectation, date_affectation: e.target.value})} type="date" required />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0 12px' }}>
            {/* Anciens — affichage seul */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Ancien site</label>
              <div style={RO_STYLE}>{formAffectation.ancien_site || '–'}</div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Nouveau site <span style={{ color: ROUGE }}>*</span></label>
              {affectationEdit ? <div style={RO_STYLE}>{formAffectation.nouveau_site || '–'}</div> : (
              <select value={formAffectation.nouveau_site_id || ''} onChange={e => setFormAffectation({...formAffectation, nouveau_site_id: e.target.value, nouveau_departement_id: '', nouveau_service_id: ''})} className="rh-champ-saisie">
                <option value="">Inchangé</option>
                {refSites.filter(s => s.actif !== false).map(s => <option key={s.id} value={s.id}>{libelleSite(s)}</option>)}
              </select>)}
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Ancien département</label>
              <div style={RO_STYLE}>{formAffectation.ancien_departement || '–'}</div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Nouveau département</label>
              {affectationEdit ? <div style={RO_STYLE}>{formAffectation.nouveau_departement || '–'}</div> : (
              <select value={formAffectation.nouveau_departement_id || ''} onChange={e => setFormAffectation({...formAffectation, nouveau_departement_id: e.target.value, nouveau_service_id: ''})} className="rh-champ-saisie">
                <option value="">{formAffectation.nouveau_site_id && formAffectation.nouveau_site_id !== employe.site_id ? 'Aucun département' : 'Inchangé'}</option>
                {refDeps.filter(d => d.actif !== false && (!d.site_id || d.site_id === (formAffectation.nouveau_site_id || employe.site_id))).map(d => <option key={d.id} value={d.id}>{d.nom}</option>)}
              </select>)}
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Ancien service</label>
              <div style={RO_STYLE}>{formAffectation.ancien_service || '–'}</div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Nouveau service</label>
              {affectationEdit ? <div style={RO_STYLE}>{formAffectation.nouveau_service || '–'}</div> : (
              <select value={formAffectation.nouveau_service_id || ''} onChange={e => setFormAffectation({...formAffectation, nouveau_service_id: e.target.value})} className="rh-champ-saisie">
                <option value="">{(formAffectation.nouveau_site_id && formAffectation.nouveau_site_id !== employe.site_id) || formAffectation.nouveau_departement_id ? 'Aucun service' : 'Inchangé'}</option>
                {refSrvs.filter(s => s.actif !== false && (!s.departement_id || s.departement_id === (formAffectation.nouveau_departement_id || employe.departement_id))).map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
              </select>)}
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Ancien poste</label>
              <div style={RO_STYLE}>{formAffectation.ancien_poste || '–'}</div>
            </div>
            <div style={{ marginBottom: 14, gridColumn: '1 / -1' }}>
              <PosteSelector
                label="Nouveau poste (référentiel)"
                valueId={formAffectation.nouveau_poste_id}
                valueLabel={formAffectation.nouveau_poste}
                onChange={(poste) => setFormAffectation({ ...formAffectation, nouveau_poste_id: poste?.id || '', nouveau_poste: poste?.intitule || '' })}
              />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Motif</label>
            <textarea value={formAffectation.motif || ''} onChange={e => setFormAffectation({...formAffectation, motif: e.target.value})} rows={2} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}><Paperclip size={13} style={{ marginRight:4 }} />Note de service (PDF/image)</label>
            <input type="file" accept=".pdf,.jpg,.png" onChange={e => setFormAffectation({...formAffectation, fichier: e.target.files[0]})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
          </div>
          {affectationEdit && (
            <div style={{ marginBottom: 14 }}>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Motif de la correction <span style={{ color: ROUGE }}>*</span></label>
              <input value={formAffectation.motif_correction || ''} onChange={e => setFormAffectation({...formAffectation, motif_correction: e.target.value})}
                placeholder="Pourquoi ce détail est-il corrigé ? (journalisé avec l'ancienne valeur)" style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
              <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Une correction ne change que la date, le motif ou le document. Si le mouvement n'aurait pas dû exister, utilisez « Annuler ».</div>
            </div>
          )}
          <BtnsModal onCancel={() => setModalAffectation(false)} onSave={soumettreAffectation} />
        </Modal>
      )}

      {annulation && (
        <Modal titre="Annuler ce mouvement" onClose={() => setAnnulation(null)}>
          <div style={{ fontSize: 13, color: '#444', marginBottom: 10 }}>
            Mouvement du <b>{fmtDate(annulation.aff.date_affectation)}</b> : {[annulation.aff.nouveau_site, annulation.aff.nouveau_departement, annulation.aff.nouveau_poste].filter(Boolean).join(' · ') || '–'}
          </div>
          {annulation.apercu.annulable ? (
            <div style={{ background: '#F1F8E9', border: '1px solid #C5E1A5', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, color: '#33691E', marginBottom: 12 }}>
              Le salarié retrouvera : {[annulation.apercu.retour.site, annulation.apercu.retour.departement, annulation.apercu.retour.service, annulation.apercu.retour.poste].filter(Boolean).join(' · ') || 'sa situation antérieure'}.
              Le mouvement restera visible dans l'historique, marqué « Annulé ».
            </div>
          ) : (
            <div style={{ background: '#FFF3E0', border: '1px solid #FFCC80', borderRadius: 8, padding: '10px 12px', fontSize: 12.5, color: '#E65100', marginBottom: 12, display: 'flex', gap: 6 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} /> {annulation.apercu.raison}
            </div>
          )}
          {annulation.apercu.annulable && (
            <>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Motif de l'annulation <span style={{ color: ROUGE }}>*</span></label>
              <textarea value={annulation.motif} onChange={e => setAnnulation({ ...annulation, motif: e.target.value })} rows={2}
                placeholder="Ex. : erreur de saisie, mauvais salarié" style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }} />
            </>
          )}
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            {annulation.apercu.annulable && (
              <button onClick={confirmerAnnulation} disabled={annulation.motif.trim().length < 5}
                style={{ flex: 1, background: annulation.motif.trim().length < 5 ? '#ccc' : ROUGE, color: '#fff', border: 'none', borderRadius: 8, padding: '10px', fontWeight: 700, cursor: 'pointer' }}>
                Annuler le mouvement
              </button>
            )}
            <button onClick={() => setAnnulation(null)} style={{ flex: 1, background: '#fff', color: '#555', border: '1px solid #ddd', borderRadius: 8, padding: '10px', fontWeight: 600, cursor: 'pointer' }}>Fermer</button>
          </div>
        </Modal>
      )}

      {modalAugmentation && (() => {
        // RH-PAIE-2I §8 — le champ "Nouveau sursalaire" ne doit plus être réutilisé tel quel
        // pour toutes les natures : Prime/Indemnité individuelles agiraient alors, à tort, sur
        // le sursalaire (un élément permanent du salaire mensuel), alors qu'elles représentent
        // un élément variable distinct. Le moteur des éléments variables de paie n'existe pas
        // encore (aucun rattachement au catalogue de rubriques, aucun calcul réel) : plutôt que
        // de simuler une prise en charge trompeuse, ces deux natures restent désactivées avec un
        // message explicite, comme demandé explicitement par la mission.
        const natureDesactivee = ['PRIME_INDIVIDUELLE', 'INDEMNITE_INDIVIDUELLE'].includes(formAugmentation.nature);
        const estCorrection = formAugmentation.nature === 'CORRECTION_ADMINISTRATIVE';
        const estSursalaire = formAugmentation.nature === 'AUGMENTATION_SURSALAIRE' || estCorrection;
        const sursalaireActuel = Number(employe?.sursalaire) || 0;
        const baseActuelle = Number(employe?.salaire_base) || 0;
        const nouveauSursalaire = formAugmentation.nouveau_sursalaire !== '' && formAugmentation.nouveau_sursalaire !== undefined ? Number(formAugmentation.nouveau_sursalaire) : null;
        const ecart = nouveauSursalaire !== null ? nouveauSursalaire - sursalaireActuel : null;
        const peutEnregistrer = formAugmentation.date_augmentation && formAugmentation.nature && !natureDesactivee
          && (!estSursalaire || (formAugmentation.nouveau_sursalaire !== '' && formAugmentation.nouveau_sursalaire !== undefined))
          && formAugmentation.motif?.trim()
          && (!estCorrection || formAugmentation.fichier || augmentationEdit?.document_url);
        return (
        <Modal titre={augmentationEdit ? 'Modifier l\'augmentation' : 'Nouvelle augmentation'} onClose={() => setModalAugmentation(false)}>
          <p style={{ fontSize: 12, color: '#777', marginTop: -6, marginBottom: 14 }}>
            Une augmentation individuelle agit sur le sursalaire — jamais sur le salaire
            catégoriel. Pour changer la catégorie ou le poste, utilisez Promotion / Reclassement.
          </p>
          <InputModal label="Date d'augmentation" value={formAugmentation.date_augmentation} onChange={e => setFormAugmentation({...formAugmentation, date_augmentation: e.target.value})} type="date" required />
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Nature <span style={{ color: ROUGE }}>*</span></label>
            <select value={formAugmentation.nature || ''} onChange={e => setFormAugmentation({...formAugmentation, nature: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, background: 'white' }}>
              <option value="">— Choisir —</option>
              <option value="AUGMENTATION_SURSALAIRE">Augmentation du sursalaire</option>
              <option value="PRIME_INDIVIDUELLE" disabled>Prime individuelle (indisponible)</option>
              <option value="INDEMNITE_INDIVIDUELLE" disabled>Indemnité individuelle (indisponible)</option>
              <option value="CORRECTION_ADMINISTRATIVE">Correction administrative (réservée DRH)</option>
            </select>
          </div>
          {natureDesactivee && (
            <div style={{ background: '#FFF3E0', border: '1px solid #FFCC80', color: '#E65100', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, fontWeight: 600, marginBottom: 14 }}>
              Fonction disponible après activation du moteur des éléments variables de paie.
            </div>
          )}
          {estSursalaire && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0 12px' }}>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Sursalaire actuel (FCFA)</label>
                  <div style={RO_STYLE}>{employe?.sursalaire ? sursalaireActuel.toLocaleString('fr-FR') + ' F' : '–'}</div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Nouveau sursalaire (FCFA) <span style={{ color: ROUGE }}>*</span></label>
                  <input type="number" value={formAugmentation.nouveau_sursalaire || ''} onChange={e => setFormAugmentation({...formAugmentation, nouveau_sursalaire: e.target.value})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </div>
              {nouveauSursalaire !== null && (
                <div style={{ background: '#F7F7F7', border: '1px solid #E0E0E0', borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 12.5 }}>
                  <div>Écart : <strong style={{ color: ecart >= 0 ? VERT : ROUGE }}>{ecart >= 0 ? '+' : ''}{ecart.toLocaleString('fr-FR')} F</strong></div>
                  <div style={{ marginTop: 4 }}>Total mensuel — avant : <strong>{(baseActuelle + sursalaireActuel).toLocaleString('fr-FR')} F</strong>, après : <strong>{(baseActuelle + nouveauSursalaire).toLocaleString('fr-FR')} F</strong></div>
                </div>
              )}
            </>
          )}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Motif {estCorrection ? '(détaillé)' : ''} <span style={{ color: ROUGE }}>*</span></label>
            <textarea value={formAugmentation.motif || ''} onChange={e => setFormAugmentation({...formAugmentation, motif: e.target.value})} rows={estCorrection ? 3 : 2} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}><Paperclip size={13} style={{ marginRight:4 }} />
              {estCorrection ? <>Référence / pièce justificative <span style={{ color: ROUGE }}>*</span></> : 'Décision d\'augmentation (PDF)'}
            </label>
            <input type="file" accept=".pdf,.jpg,.png" onChange={e => setFormAugmentation({...formAugmentation, fichier: e.target.files[0]})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
            {estCorrection && <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>Une correction administrative exige toujours une référence documentaire — jamais un simple ajustement silencieux.</div>}
          </div>
          <BtnsModal onCancel={() => setModalAugmentation(false)} onSave={soumettreAugmentation} disabled={!peutEnregistrer} />
        </Modal>
        );
      })()}

      {modalPromotion && (() => {
        const categorieChoisie = categoriesPourPromotion.find((c) => c.code === formPromotion.nouvelle_categorie && c.famille_professionnelle === formPromotion.nouvelle_famille_professionnelle);
        const echelonsDisponibles = categorieChoisie?.echelons_disponibles || [];
        // RH-PAIE-2I §9B — un changement de poste SEUL (famille/catégorie/échelon identiques ou
        // non renseignés) n'est jamais présenté à tort comme une promotion salariale : jamais de
        // hiérarchie inventée entre intitulés de postes, une confirmation explicite est requise.
        const changementCategoriel = !!formPromotion.nouvelle_categorie;
        const posteSeulChange = !!formPromotion.nouveau_titre && !changementCategoriel;
        const sursalaireActuel = Number(employe?.sursalaire) || 0;
        const baseActuelle = Number(employe?.salaire_base) || 0;
        const peutEnregistrer = formPromotion.date_promotion && formPromotion.motif?.trim()
          && (formPromotion.nouveau_titre || changementCategoriel)
          && (!changementCategoriel || formPromotion.nouvelle_famille_professionnelle)
          && (!posteSeulChange || formPromotion.confirmationPosteSeul);
        return (
        <Modal titre={promotionEdit ? 'Modifier la promotion' : 'Nouvelle promotion'} onClose={() => setModalPromotion(false)}>
          <InputModal label="Date de promotion" value={formPromotion.date_promotion} onChange={e => setFormPromotion({...formPromotion, date_promotion: e.target.value})} type="date" required />
          <div style={{ marginBottom: 14 }}>
            <PosteSelector
              label="Nouveau poste (référentiel, optionnel si seule la catégorie change)"
              valueId={formPromotion.nouveau_poste_id}
              valueLabel={formPromotion.nouveau_titre}
              onChange={(poste) => setFormPromotion({ ...formPromotion, nouveau_poste_id: poste?.id || '', nouveau_titre: poste?.intitule || '', confirmationPosteSeul: false })}
            />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Nouvelle famille professionnelle</label>
            <select value={formPromotion.nouvelle_famille_professionnelle} onChange={(e) => setFormPromotion({ ...formPromotion, nouvelle_famille_professionnelle: e.target.value, nouvelle_categorie: '', nouvel_echelon: '' })}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}>
              <option value="">Sélectionner une famille</option>
              {Object.entries(FAMILLES_PROFESSIONNELLES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 14 }}>
            <CategorieSelector
              label="Nouvelle catégorie (le salaire catégoriel viendra de la grille salariale)"
              value={formPromotion.nouvelle_categorie}
              onChange={(v) => setFormPromotion({ ...formPromotion, nouvelle_categorie: v, nouvel_echelon: '' })}
              disabled={!formPromotion.nouvelle_famille_professionnelle}
              familleProfessionnelle={formPromotion.nouvelle_famille_professionnelle || undefined}
            />
          </div>
          {changementCategoriel && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0 12px' }}>
              {echelonsDisponibles.length > 0 && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Échelon</label>
                  <select value={formPromotion.nouvel_echelon} onChange={(e) => setFormPromotion({ ...formPromotion, nouvel_echelon: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, background: 'white' }}>
                    <option value="">Sans échelon</option>
                    {echelonsDisponibles.map((ech) => <option key={ech} value={ech}>{ech}</option>)}
                  </select>
                </div>
              )}
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Statut cadre</label>
                <select value={formPromotion.nouveau_statut_cadre} onChange={(e) => setFormPromotion({ ...formPromotion, nouveau_statut_cadre: e.target.value })} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, background: 'white' }}>
                  <option value="">Déduit automatiquement de la famille</option>
                  <option value="cadre">Cadre</option>
                  <option value="non_cadre">Non-cadre</option>
                </select>
              </div>
            </div>
          )}
          {posteSeulChange && (
            <div style={{ background: '#FFF3E0', border: '1px solid #FFCC80', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12.5 }}>
              <div style={{ fontWeight: 700, color: '#E65100', marginBottom: 6 }}>Changement de poste sans changement catégoriel</div>
              <div style={{ color: '#555', marginBottom: 8 }}>
                Aucune règle n'associe automatiquement un intitulé de poste à une famille ou une catégorie. Cette opération ne modifiera ni la famille, ni la catégorie, ni l'échelon, ni le salaire — uniquement le poste occupé.
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={formPromotion.confirmationPosteSeul} onChange={(e) => setFormPromotion({ ...formPromotion, confirmationPosteSeul: e.target.checked })} style={{ marginTop: 2 }} />
                <span>Je confirme qu'il s'agit d'un changement de poste fonctionnel, sans effet salarial.</span>
              </label>
            </div>
          )}
          {(formPromotion.nouveau_titre || changementCategoriel) && (
            <div style={{ background: '#F1F8E9', border: '1px solid #C8E6C9', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12.5 }}>
              <div style={{ fontWeight: 700, color: VERT, marginBottom: 4 }}>Aperçu</div>
              <div>Poste : {employe?.poste || '–'} <ArrowRight size={12} color={VERT} style={{ verticalAlign: 'middle' }} aria-label="devient" /> {formPromotion.nouveau_titre || employe?.poste || '–'}</div>
              <div>Catégorie : {employe?.categorie ? `${familleProfessionnelle ? humaniserFamilleProfessionnelle(familleProfessionnelle) : 'À classer'} — ${employe.categorie}` : '–'} <ArrowRight size={12} color={VERT} style={{ verticalAlign: 'middle' }} aria-label="devient" /> {formPromotion.nouvelle_categorie ? `${formPromotion.nouvelle_famille_professionnelle ? humaniserFamilleProfessionnelle(formPromotion.nouvelle_famille_professionnelle) : ''} — ${formPromotion.nouvelle_categorie}` : (employe?.categorie ? `${familleProfessionnelle ? humaniserFamilleProfessionnelle(familleProfessionnelle) : 'À classer'} — ${employe.categorie}` : '–')}</div>
              {changementCategoriel && (
                <>
                  <div>Échelon : {'–'} <ArrowRight size={12} color={VERT} style={{ verticalAlign: 'middle' }} aria-label="devient" /> {formPromotion.nouvel_echelon || 'Sans échelon'}</div>
                  <div style={{ marginTop: 6, paddingTop: 6, borderTop: '1px solid #C8E6C9' }}>
                    Salaire de base : <strong>{baseActuelle ? baseActuelle.toLocaleString('fr-FR') + ' F' : '–'}</strong> <ArrowRight size={12} color={VERT} style={{ verticalAlign: 'middle' }} aria-label="devient" /> <strong>
                      {salaireBaseProposePromotion === undefined ? '…' : salaireBaseProposePromotion === null ? <span style={{ color: ROUGE }}>aucune grille validée pour cette combinaison</span> : salaireBaseProposePromotion.toLocaleString('fr-FR') + ' F'}
                    </strong>
                  </div>
                  <div>Sursalaire (inchangé) : <strong>{sursalaireActuel.toLocaleString('fr-FR')} F</strong></div>
                  {typeof salaireBaseProposePromotion === 'number' && (
                    <div style={{ marginTop: 4 }}>Total mensuel — avant : <strong>{(baseActuelle + sursalaireActuel).toLocaleString('fr-FR')} F</strong>, après : <strong>{(salaireBaseProposePromotion + sursalaireActuel).toLocaleString('fr-FR')} F</strong></div>
                  )}
                  <div style={{ color: '#777', marginTop: 6 }}>Le nouveau salaire catégoriel sera résolu depuis la grille salariale validée à cette date d'effet — jamais une valeur devinée.</div>
                </>
              )}
            </div>
          )}
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}>Motif <span style={{ color: ROUGE }}>*</span></label>
            <textarea value={formPromotion.motif || ''} onChange={e => setFormPromotion({...formPromotion, motif: e.target.value})} rows={2} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box', resize: 'vertical' }} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4 }}><Paperclip size={13} style={{ marginRight:4 }} />Acte de promotion (PDF)</label>
            <input type="file" accept=".pdf,.jpg,.png" onChange={e => setFormPromotion({...formPromotion, fichier: e.target.files[0]})} style={{ width: '100%', padding: '8px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
          </div>
          <BtnsModal onCancel={() => setModalPromotion(false)} onSave={soumettrePromotion} disabled={!peutEnregistrer} />
        </Modal>
        );
      })()}

      {modalEnfant && (
        <Modal titre={enfantEdit ? 'Modifier enfant' : 'Ajouter un enfant'} onClose={() => setModalEnfant(false)}>
          <InputModal label="Prénom" value={formEnfant.prenom} onChange={e => setFormEnfant({...formEnfant, prenom: e.target.value})} required />
          <InputModal label="Nom" value={formEnfant.nom} onChange={e => setFormEnfant({...formEnfant, nom: e.target.value})} />
          <InputModal label="Date de naissance" value={formEnfant.date_naissance} onChange={e => setFormEnfant({...formEnfant, date_naissance: e.target.value})} type="date" required />
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--sg-texte-2)', marginBottom: 4 }}><Paperclip size={13} style={{ marginRight:4 }} />{enfantEdit ? (enfantEdit.acte_naissance_url ? 'Remplacer l\'acte de naissance (PDF ou image)' : 'Acte de naissance (PDF ou image)') : 'Acte de naissance (PDF ou image)'}{!enfantEdit && <span style={{ color: 'var(--sg-danger)' }}> *</span>}</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setFormEnfant({...formEnfant, fichier_acte: e.target.files[0]})} className="rh-champ-saisie" style={{ padding: '6px 10px' }} />
            <div style={{ fontSize: 12, color: 'var(--sg-texte-3)', marginTop: 4 }}>
              {enfantEdit ? (enfantEdit.acte_naissance_url ? 'Une pièce est déposée. Une nouvelle pièce devra être vérifiée à nouveau par un RH.' : 'Aucune pièce déposée : déposez l\'acte pour régulariser. Sans acte vérifié, aucun droit n\'est ouvert (IPM).') : 'Obligatoire. La pièce devra ensuite être vérifiée par un RH pour ouvrir les droits (IPM).'}
            </div>
          </div>
          <SelectModal label="Sexe *" value={formEnfant.sexe} onChange={e => setFormEnfant({...formEnfant, sexe: e.target.value})} options={[{v:'',l:'Sélectionner'},{v:'M',l:'Garçon'},{v:'F',l:'Fille'}]} />
          <BtnsModal onCancel={() => setModalEnfant(false)} onSave={soumettreEnfant} />
        </Modal>
      )}

      {/* Modal Dossier numérique */}
      {modalDossier && (
        <ModalDossier
          dossier={dossier}
          loading={loadingDossier}
          onClose={() => { setModalDossier(false); setDossier(null); }}
          onDownloadPDF={telechargerDossierPDF}
          loadingPDF={loadingPDF}
        />
      )}

      {/* Module 1 (livraison 2) : actions métier nommées */}
      {actionOuverte === 'contrat' && <ModaleContrat salarieId={id} contrat={synthese?.contrat} nom={`${employe.prenom} ${(employe.nom || '').toUpperCase()}`} onFermer={() => setActionOuverte(null)} onTermine={(m) => { showMsg(m); actionTerminee(); }} />}
      {actionOuverte === 'sortie' && <ModaleSortie salarieId={id} nom={`${employe.prenom} ${(employe.nom || '').toUpperCase()}`} motifs={motifs.sortie} peutLicencier={!!synthese?.actions?.licenciement} onFermer={() => setActionOuverte(null)} onTermine={(m) => { showMsg(m); actionTerminee(); }} />}
      {['suspension', 'reintegration'].includes(actionOuverte) && <ModaleStatut salarieId={id} mode={actionOuverte} nom={`${employe.prenom} ${(employe.nom || '').toUpperCase()}`} motifs={motifs.suspension} onFermer={() => setActionOuverte(null)} onTermine={(m) => { showMsg(m); actionTerminee(); }} />}
      {actionOuverte === 'responsable' && <ModaleResponsable salarieId={id} responsable={synthese?.responsable} nom={`${employe.prenom} ${(employe.nom || '').toUpperCase()}`} onFermer={() => setActionOuverte(null)} onTermine={(m) => { showMsg(m); actionTerminee(); }} />}
      {actionOuverte === 'metier' && <ModaleMetier salarieId={id} metier={synthese?.metier} nom={`${employe.prenom} ${(employe.nom || '').toUpperCase()}`} onFermer={() => setActionOuverte(null)} onTermine={(m) => { showMsg(m); actionTerminee(); }} />}
      {annulationDemandee && (
        <ModaleAnnulation
          titre={`Annuler : ${annulationDemandee.titre}`}
          description="Pour une saisie faite par erreur. L'historique garde la trace de l'événement et de son annulation."
          url={annulationDemandee.type === 'contrat' ? `/api/salaries/${id}/actions/contrat/${annulationDemandee.id}/annuler` : `/api/salaries/${id}/evenements/${annulationDemandee.id}/annuler`}
          onFermer={() => setAnnulationDemandee(null)} onTermine={(m) => { showMsg(m); actionTerminee(); }} />
      )}

      {/* Modale de confirmation SOGAS */}
      <ConfirmModal action={confirmAction} onClose={() => setConfirmAction(null)} />

    </Layout>
  );
}
